import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate, uploadFile } from '../shared/api';
import { API_BASE } from '../shared/constants';
import { formatDate, formatMoney, formatNumber } from '../shared/formatting';
import { PersonName } from '../shared/PersonName';
import { readUrlParam, useUrlSync, ProgressOverlay, type ProgressOverlayState } from '../shared/urlState';

type Locale = 'hr' | 'en';

function KpiMini({ label, value, accent, sublabel }: { label: string; value: string; accent: string; sublabel?: string }) {
    return (
        <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 6, padding: '6px 10px' }}>
            <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(0,0,0,0.5)' }}>{label}</div>
            <strong style={{ fontSize: '1rem', color: accent }}>{value}</strong>
            {sublabel ? <div className="muted" style={{ fontSize: '0.7rem' }}>{sublabel}</div> : null}
        </div>
    );
}

const DeliveryPlanTabLazy = React.lazy(() => import('./goldCmModule').then((m) => ({ default: m.DeliveryPlanTab })));
export function PlanIsporukaInPlanerTab({ token, locale }: { token: string; locale: Locale }) {
    return (
        <React.Suspense fallback={<p className="muted">Učitavam Plan isporuka…</p>}>
            <DeliveryPlanTabLazy token={token} locale={locale as 'hr' | 'en'} />
        </React.Suspense>
    );
}

type Batch = {
    id: number;
    kind: 'open_orders' | 'stock' | 'supplier_map';
    filename: string;
    row_count: number;
    completed_at: string;
    snapshot_date: string | null;
    source: string;
};

type Warehouse = {
    id: number;
    code: string;
    name: string;
    is_default_visible: 0 | 1 | boolean;
};

type StateResponse = {
    last_batches: Record<string, Batch>;
    visible_warehouses: string[];
    all_warehouses: Warehouse[];
    suppliers: string[];
};

type BacklogItem = {
    order_number: string;
    branch_code: string | null;
    branch_name: string | null;
    partner_name: string | null;
    partner_name_is_person?: boolean;
    item_name: string;
    item_date: string | null;
    qty: number;
    fine_gold_g?: number;
    unit_premium_eur?: number;
    premium_source?: string;
    total_eur: number;
    cost_estimate_eur?: number;
    ruc_estimate_eur?: number;
    remaining_eur: number;
    mint_category: string | null;
    supplier: string;
};

type BacklogSupplier = {
    supplier: string;
    items_count: number;
    total_eur: number;
    remaining_eur: number;
    cost_estimate_eur: number;
    ruc_estimate_eur: number;
    margin_pct: number;
    oldest_date: string | null;
};

type BacklogOrder = {
    order_number: string;
    partner_name: string | null;
    branch: string | null;
    item_date: string | null;
    total_eur: number;
    cost_estimate_eur: number;
    ruc_estimate_eur: number;
    margin_pct: number;
    remaining_eur: number;
    paid_eur: number;
    paid_pct: number;
    payment_status?: 'paid_full' | 'paid_partial' | 'unpaid';
    items_count: number;
    suppliers: string[];
    is_ignored?: boolean;
    coverage?: 'full' | 'partial' | 'none';
    coverage_breakdown?: { covered: number; partial: number; uncovered: number };
    metals?: string[]; // npr ['Au'] | ['Ag'] | ['Au','Ag']
};

type BacklogResponse = {
    open_batch_id: number | null;
    by_supplier: BacklogSupplier[];
    by_branch: Array<{ branch: string; items_count: number }>;
    by_order: BacklogOrder[];
    items: BacklogItem[];
    spot?: { spot_date: string; price_eur_per_troy_oz: number; price_eur_per_g: number } | null;
    premium_stats?: {
        revision_id: number | null;
        products_with_cgm_premium: number;
        sources: Record<string, number>;
    };
    ignored_count?: number;
    coverage_stats?: {
        orders_fully_covered: number;
        orders_partial: number;
        orders_uncovered: number;
        show_uncovered_only_filter?: boolean;
    };
    message?: string;
};

type RecLinkedOrder = {
    order_number: string;
    partner_name: string | null;
    item_date: string | null;
    qty_used: number;
    qty_total_for_item: number;
    sale_eur_share: number;
};

type RecRow = {
    item_code: string;
    item_name: string;
    qty_needed: number;
    qty_on_hand: number;
    qty_recommended: number;
    qty_uncovered?: number;
    unit_price_eur: number;
    total_cost_eur: number;
    total_sales_eur?: number;
    ruc_estimate_eur?: number;
    margin_pct?: number;
    oldest_date: string | null;
    linked_orders?: RecLinkedOrder[];
};

type RecOrderSummary = {
    order_number: string;
    partner_name: string | null;
    item_date: string | null;
    items_count: number;
    qty_total: number;
    sale_eur: number;
};

type RecResponse = {
    supplier: string;
    budget_eur: number;
    budget_remaining_eur: number;
    visible_warehouses: string[];
    recommendation: RecRow[];
    total_cost_eur: number;
    total_sales_eur?: number;
    total_ruc_estimate_eur?: number;
    margin_pct?: number;
    linked_orders?: RecOrderSummary[];
    message?: string;
};

type Subtab = 'upload' | 'backlog' | 'recommendation' | 'reserved' | 'lager';

type ForecastDay = {
    date: string;
    inflows_certain_eur: number;
    inflows_expected_eur: number;
    outflows_scheduled_eur: number;
    opening_balance_eur: number;
    ending_balance_eur: number;
    alert: 'negative' | 'low' | null;
};

type ForecastResponse = {
    today: string;
    horizon_days: number;
    starting_balance_eur: number;
    starting_balance_as_of: string | null;
    days: ForecastDay[];
    totals: {
        inflows_certain_eur: number;
        inflows_expected_eur: number;
        outflows_scheduled_eur: number;
        lowest_balance_eur: number;
        lowest_balance_date: string | null;
    };
    alerts: Array<{ date: string; kind: string; message: string; balance_eur: number }>;
};

type CashRow = {
    id: number;
    supplier_name: string;
    commitment_eur: number;
    committed_at: string;
    due_date: string;
    paid_at: string | null;
    paid_eur: number | null;
    status: string;
    order_reference: string | null;
    notes: string | null;
    is_overdue: boolean;
    days_to_due: number | null;
};

type CashSupplierSummary = {
    supplier_name: string;
    open_eur: number;
    open_count: number;
    credit_limit_eur: number | null;
    usage_pct: number | null;
    default_days_to_pay: number;
};

type CashResponse = {
    rows: CashRow[];
    summary: {
        by_supplier: CashSupplierSummary[];
        total_open_eur: number;
        due_in_14_days_eur: number;
        overdue_eur: number;
    };
};

export function GoldCmPlannerPanel({ token, locale, onOpenPlanGenerate }: { token: string; locale: Locale; onOpenPlanGenerate?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [subtab, setSubtab] = useState<Subtab>(() => {
        const t = readUrlParam('sub', 'upload');
        const valid: Subtab[] = ['upload', 'backlog', 'recommendation', 'reserved', 'lager'];
        return (valid.includes(t as Subtab) ? t : 'upload') as Subtab;
    });
    useUrlSync('sub', subtab, subtab === 'upload');
    const [state, setState] = useState<StateResponse | null>(null);
    const [stateLoading, setStateLoading] = useState(true);
    const [error, setError] = useState('');

    const loadState = useCallback(async () => {
        setStateLoading(true);
        try {
            const r = await api<StateResponse>('/gold-cm-planner/state', token);
            setState(r);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setStateLoading(false);
        }
    }, [token]);

    useEffect(() => { void loadState(); }, [loadState]);

    return (
        <div className="card" style={{ padding: '14px 18px', background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--ad-ink)' }}>
                    📦 {t('Gold Category Manager — Planer (40.1)', 'Gold Category Manager — Planner (40.1)')}
                </h3>
                {state ? (
                    <span className="muted" style={{ fontSize: '0.78rem' }}>
                        {state.suppliers.length} {t('dobavljača', 'suppliers')} ·{' '}
                        {state.visible_warehouses.length}/{state.all_warehouses.length} {t('skladišta vidljiva', 'warehouses visible')}
                    </span>
                ) : null}
            </div>

            {error ? <p className="error">{error}</p> : null}

            <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.25)', paddingBottom: 8 }}>
                {([
                    { id: 'upload' as Subtab, icon: '📥', label: t('Upload', 'Upload') },
                    { id: 'backlog' as Subtab, icon: '📋', label: t('Backlog + filteri', 'Backlog + filters') },
                    { id: 'recommendation' as Subtab, icon: '🎯', label: t('Preporuka narudžbe', 'Order recommendation') },
                    { id: 'reserved' as Subtab, icon: '🔒', label: t('Rezervirano (poslovnica)', 'Reserved (branch)') },
                    { id: 'lager' as Subtab, icon: '📊', label: t('Lager plan 60×12', 'Lager plan 60×12') },
                ]).map((cfg) => {
                    const active = subtab === cfg.id;
                    return (
                        <button
                            key={cfg.id}
                            type="button"
                            onClick={() => setSubtab(cfg.id)}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 8,
                                border: active ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                                background: active ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                                color: active ? '#fffbeb' : 'var(--ad-ink)',
                                fontWeight: 600,
                                fontSize: '0.86rem',
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 6,
                            }}
                        >
                            <span>{cfg.icon}</span>
                            <span>{cfg.label}</span>
                        </button>
                    );
                })}
            </div>

            {stateLoading ? <p className="muted">{t('Učitavam stanje…', 'Loading state…')}</p> : null}

            {subtab === 'upload' && state ? <UploadTab token={token} locale={locale} state={state} onReload={loadState} /> : null}
            {subtab === 'backlog' && state ? <BacklogTab token={token} locale={locale} state={state} onGoToPlan={onOpenPlanGenerate ?? (() => {})} /> : null}
            {subtab === 'recommendation' && state ? <RecommendationTab token={token} locale={locale} state={state} /> : null}
            {subtab === 'reserved' && state ? <BranchReservedTab token={token} locale={locale} state={state} /> : null}
            {subtab === 'lager' && state ? <LagerPlanTab token={token} locale={locale} /> : null}
        </div>
    );
}

/* ── Tab Upload ───────────────────────────────────────────────────── */

function UploadTab({ token, locale, state, onReload }: { token: string; locale: Locale; state: StateResponse; onReload: () => void }) {
    return (
        <>
            <SmartIngestActionRow token={token} locale={locale} onReload={onReload} />
            <ImportBatchesManager token={token} locale={locale} onReload={onReload} />
            <RecentUploadsLog token={token} locale={locale} reloadKey={state.last_batches?.open_orders?.id ?? 0} />
        </>
    );
}

/**
 * Lista import batches per kind + brisanje. Nakon brisanja, sljedeći max(batch_id) postaje aktivan.
 * Preporuka / plan isporuka / backlog se na svakom click-u izračunavaju na trenutnom aktivnom batch-u.
 */
function ImportBatchesManager({ token, locale, onReload }: { token: string; locale: Locale; onReload: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = React.useState<{ batches: any[]; effective: Record<string, number | null> } | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [show, setShow] = React.useState(false);

    const load = React.useCallback(async () => {
        try {
            const r = await fetch('/api/gold-cm-planner/import-batches', { headers: { Authorization: `Bearer ${token}` } });
            if (r.ok) setData(await r.json());
        } catch { /* */ }
    }, [token]);
    React.useEffect(() => { if (show) void load(); }, [show, load]);

    async function deleteBatch(id: number, kind: string) {
        if (!confirm(t(`Obrisati ${kind} batch #${id}? Svi redovi tog batcha bit će uklonjeni. Sljedeći batch postaje aktivan.`, `Delete ${kind} batch #${id}? All rows will be removed. Next batch becomes active.`))) return;
        setBusy(true);
        try {
            const r = await fetch(`/api/gold-cm-planner/import-batches/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
            if (r.ok) {
                await load();
                onReload();
            } else {
                const body = await r.json().catch(() => null) as { error?: string; message?: string } | null;
                alert(body?.error ?? body?.message ?? t('Greška', 'Error'));
            }
        } finally { setBusy(false); }
    }

    if (!show) {
        return (
            <div style={{ marginBottom: 12 }}>
                <button type="button" className="secondary compact" onClick={() => setShow(true)} style={{ fontSize: '0.78rem' }}>
                    📚 {t('Povijest uvoza & brisanje', 'Import history & delete')}
                </button>
            </div>
        );
    }
    if (!data) return <p className="muted" style={{ fontSize: '0.8rem' }}>{t('Učitavam…', 'Loading…')}</p>;

    return (
        <div className="card" style={{ padding: '12px 14px', marginBottom: 12, background: 'rgba(255,250,230,0.7)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong style={{ fontSize: '0.86rem' }}>📚 {t('Povijest uvoza per vrsta', 'Import history per kind')}</strong>
                <button type="button" onClick={() => setShow(false)} className="secondary compact" style={{ fontSize: '0.74rem' }}>✕</button>
            </div>
            <p className="muted" style={{ fontSize: '0.72rem', marginBottom: 8, lineHeight: 1.5 }}>
                ℹ️ {t(
                    'Aktivni batch (✓) je onaj na kojem rade preporuka / plan isporuka / backlog. Brisanje aktivnog vraća prethodni kao aktivan na sljedećem klik-u.',
                    'Active batch (✓) is what the recommendation / delivery plan / backlog query. Deleting active falls back to previous on next click.',
                )}
            </p>
            <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
                <table className="admin-user-table" style={{ fontSize: '0.76rem' }}>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>{t('Vrsta', 'Kind')}</th>
                            <th>{t('Datoteka', 'File')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Redaka', 'Rows')}</th>
                            <th>{t('Datum', 'Date')}</th>
                            <th>{t('Uvezao', 'Uploaded by')}</th>
                            <th>{t('Aktivan', 'Active')}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {data.batches.map((b: any) => (
                            <tr key={b.id} style={b.is_active ? { background: 'rgba(31,122,54,0.08)', fontWeight: 600 } : undefined}>
                                <td>{b.id}</td>
                                <td>{b.kind}</td>
                                <td style={{ fontSize: '0.7rem' }}>{b.filename?.slice(0, 50) ?? '—'}</td>
                                <td style={{ textAlign: 'right' }}>{b.row_count ?? 0}</td>
                                <td className="muted" style={{ fontSize: '0.7rem' }}>{b.completed_at?.replace('T', ' ').slice(0, 16) ?? '—'}</td>
                                <td className="muted" style={{ fontSize: '0.7rem' }}>{b.uploaded_by_name ?? '—'}</td>
                                <td>{b.is_active ? <span style={{ color: '#1f7a36', fontWeight: 700 }}>✓ AKTIVAN</span> : <span className="muted">—</span>}</td>
                                <td>
                                    <button type="button" disabled={busy} onClick={() => void deleteBatch(b.id, b.kind)} className="secondary compact" style={{ fontSize: '0.7rem', color: '#b14040' }}>
                                        🗑 {t('Obriši', 'Delete')}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ── Tab Backlog ─────────────────────────────────────────────────── */

type SortKey = 'date' | 'order' | 'partner' | 'items' | 'total' | 'cost' | 'ruc' | 'paid' | 'remaining' | 'pct';
type SortDir = 'asc' | 'desc';

function BacklogTab({ token, locale, state, onGoToPlan }: { token: string; locale: Locale; state: StateResponse; onGoToPlan: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<BacklogResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeSuppliers, setActiveSuppliers] = useState<Set<string>>(new Set());
    const [sortKey, setSortKey] = useState<SortKey>('remaining');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [creating, setCreating] = useState(false);
    const [createResult, setCreateResult] = useState<Array<{ id: number; supplier_name: string; due_date: string }> | null>(null);
    const [createError, setCreateError] = useState('');
    const [drawerOrder, setDrawerOrder] = useState<BacklogOrder | null>(null);
    const [showUncoveredOnly, setShowUncoveredOnly] = useState(false);
    const [showMixedOnly, setShowMixedOnly] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError(''); setSelected(new Set()); setCreateResult(null);
        try {
            // Uvijek dohvati i ignorirane — one ostaju vidljive u listi (samo precrtano + checkbox check).
            const qs = new URLSearchParams({ include_ignored: '1' });
            if (showUncoveredOnly) qs.set('show_uncovered_only', '1');
            setData(await api<BacklogResponse>(`/gold-cm-planner/backlog?${qs.toString()}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, showUncoveredOnly]);

    async function toggleIgnore(orderNumber: string) {
        try {
            await mutate(`/gold-cm-planner/orders/${encodeURIComponent(orderNumber)}/toggle-ignore`, token, 'POST', {});
            await load();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'API');
        }
    }

    useEffect(() => { void load(); }, [load]);

    function toggleSort(k: SortKey) {
        if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        else { setSortKey(k); setSortDir(k === 'order' || k === 'partner' ? 'asc' : 'desc'); }
    }

    function toggleSupplier(s: string) {
        const next = new Set(activeSuppliers);
        if (next.has(s)) next.delete(s); else next.add(s);
        setActiveSuppliers(next);
        setSelected(new Set()); // reset selection on filter change
    }

    const filteredOrders = useMemo(() => {
        if (!data?.by_order) return [];
        let list = activeSuppliers.size === 0
            ? data.by_order
            : data.by_order.filter((o) => o.suppliers.some((s) => activeSuppliers.has(s)));
        if (showMixedOnly) {
            list = list.filter((o) => (o.metals ?? []).includes('Au') && (o.metals ?? []).includes('Ag'));
        }
        const sorted = [...list];
        const mul = sortDir === 'asc' ? 1 : -1;
        const cmp: Record<SortKey, (a: BacklogOrder, b: BacklogOrder) => number> = {
            date: (a, b) => mul * String(a.item_date ?? '').localeCompare(String(b.item_date ?? '')),
            order: (a, b) => mul * a.order_number.localeCompare(b.order_number),
            partner: (a, b) => mul * String(a.partner_name ?? '').localeCompare(String(b.partner_name ?? '')),
            items: (a, b) => mul * (a.items_count - b.items_count),
            total: (a, b) => mul * (a.total_eur - b.total_eur),
            cost: (a, b) => mul * (a.cost_estimate_eur - b.cost_estimate_eur),
            ruc: (a, b) => mul * (a.ruc_estimate_eur - b.ruc_estimate_eur),
            paid: (a, b) => mul * (a.paid_eur - b.paid_eur),
            remaining: (a, b) => mul * (a.remaining_eur - b.remaining_eur),
            pct: (a, b) => mul * (a.paid_pct - b.paid_pct),
        };
        sorted.sort(cmp[sortKey]);
        return sorted;
    }, [data, activeSuppliers, sortKey, sortDir, showMixedOnly]);

    function toggleSelect(orderNum: string) {
        const next = new Set(selected);
        if (next.has(orderNum)) next.delete(orderNum); else next.add(orderNum);
        setSelected(next);
    }

    function toggleSelectAll() {
        if (selected.size === filteredOrders.length) setSelected(new Set());
        else setSelected(new Set(filteredOrders.map((o) => o.order_number)));
    }

    // Selected orders grouped by supplier (proporcionalna raspodjela ako narudžba ima više dobavljača)
    const selectedGroups = useMemo(() => {
        const map: Record<string, { supplier: string; orders: BacklogOrder[]; total_eur: number; cost_eur: number; ruc_eur: number; commitment_eur: number }> = {};
        for (const o of filteredOrders) {
            if (!selected.has(o.order_number)) continue;
            // BUGFIX: kad je filter dobavljača aktivan, pripremaj SAMO za odabrane dobavljače —
            // inače multi-supplier narudžba (npr. Argor+StoneX) generira i narudžbe za neodabrane.
            const relevant = activeSuppliers.size === 0 ? o.suppliers : o.suppliers.filter((s) => activeSuppliers.has(s));
            if (relevant.length === 0) continue;
            const n = Math.max(1, relevant.length);
            const splitTotal = o.total_eur / n;
            const splitCost = o.cost_estimate_eur / n;
            const splitCommit = o.remaining_eur / n;
            for (const s of relevant) {
                if (!map[s]) map[s] = { supplier: s, orders: [], total_eur: 0, cost_eur: 0, ruc_eur: 0, commitment_eur: 0 };
                map[s].orders.push(o);
                map[s].total_eur += splitTotal;
                map[s].cost_eur += splitCost;
                map[s].ruc_eur += splitTotal - splitCost;
                map[s].commitment_eur += splitCommit;
            }
        }
        return Object.values(map).sort((a, b) => b.total_eur - a.total_eur);
    }, [filteredOrders, selected]);

    const selectedTotals = useMemo(() => {
        const t = selectedGroups.reduce(
            (acc, g) => ({ total: acc.total + g.total_eur, cost: acc.cost + g.cost_eur, ruc: acc.ruc + g.ruc_eur }),
            { total: 0, cost: 0, ruc: 0 },
        );
        return { ...t, margin_pct: t.total > 0 ? (100 * t.ruc) / t.total : 0 };
    }, [selectedGroups]);

    async function createOrders() {
        if (selectedGroups.length === 0) return;
        const realSuppliers = selectedGroups.filter((g) => g.supplier !== 'Neraspoređeno');
        if (realSuppliers.length === 0) {
            setCreateError(t('Odabir sadrži samo neraspoređene artikle — ne mogu kreirati narudžbe.', 'Selection has only unassigned items — cannot create orders.'));
            return;
        }
        const confirm = window.confirm(
            t(`Kreirat ću ${realSuppliers.length} narudžb${realSuppliers.length === 1 ? 'u' : 'i'} u Cash Schedule (po jedna per dobavljač)?`,
              `Create ${realSuppliers.length} order${realSuppliers.length === 1 ? '' : 's'} in Cash Schedule (one per supplier)?`)
        );
        if (!confirm) return;
        setCreating(true); setCreateError(''); setCreateResult(null);
        const results: Array<{ id: number; supplier_name: string; due_date: string }> = [];
        try {
            for (const g of realSuppliers) {
                const orderRefs = g.orders.map((o) => o.order_number).join(', ');
                const r = await mutate<{ id: number; due_date: string }>('/gold-cm-planner/cash-schedule', token, 'POST', {
                    supplier_name: g.supplier,
                    commitment_eur: g.cost_eur,  // nabavna vrijednost = što plaćamo dobavljaču
                    committed_at: new Date().toISOString().slice(0, 10),
                    order_reference: `BACKLOG: ${orderRefs}`.slice(0, 191),
                    recommendation_snapshot: { source: 'backlog_bulk', sales_eur: g.total_eur, cost_eur: g.cost_eur, ruc_eur: g.ruc_eur, orders: g.orders.map((o) => ({ order_number: o.order_number, partner: o.partner_name, total: o.total_eur, cost: o.cost_estimate_eur, ruc: o.ruc_estimate_eur })) },
                });
                results.push({ id: r.id, supplier_name: g.supplier, due_date: r.due_date });
            }
            setCreateResult(results);
            setSelected(new Set());
        } catch (e) {
            setCreateError(e instanceof Error ? e.message : 'API');
        } finally {
            setCreating(false);
        }
    }

    const [exporting, setExporting] = useState(false);
    async function exportXlsx() {
        setExporting(true);
        try {
            const qs = new URLSearchParams();
            if (activeSuppliers.size === 1) qs.set('supplier', Array.from(activeSuppliers)[0]);
            const res = await fetch(`${API_BASE}/gold-cm-planner/backlog/export.xlsx?${qs.toString()}`, {
                headers: { Accept: 'application/octet-stream', Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
            a.download = `Gold_CM_Backlog_${stamp}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            alert(e instanceof Error ? e.message : 'Export error');
        } finally {
            setExporting(false);
        }
    }

    async function sendAllPdfs() {
        if (!createResult) return;
        let ok = 0; let fail = 0;
        for (const r of createResult) {
            try {
                await mutate(`/gold-cm-planner/cash-schedule/${r.id}/send-pdf`, token, 'POST', {});
                ok++;
            } catch { fail++; }
        }
        alert(t(`PDF: ${ok} OK, ${fail} greška.`, `PDF: ${ok} OK, ${fail} errors.`));
    }

    if (loading && !data) return <p className="muted">{t('Učitavam backlog…', 'Loading backlog…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;
    if (data.message && !data.items.length) return <p className="muted">{data.message}</p>;

    const SortHeader = ({ k, label, align }: { k: SortKey; label: string; align?: 'right' }) => (
        <th
            onClick={() => toggleSort(k)}
            style={{ cursor: 'pointer', userSelect: 'none', textAlign: align, whiteSpace: 'nowrap' }}
            title={t('Klikni za sortiranje', 'Click to sort')}
        >
            {label}
            <span style={{ marginLeft: 4, opacity: sortKey === k ? 1 : 0.25, fontSize: '0.78em' }}>
                {sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
            </span>
        </th>
    );

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                <p className="muted" style={{ fontSize: '0.82rem', margin: 0, flex: 1, minWidth: 280 }}>
                    {t('Klikni karticu dobavljača da filtriraš · klikni naslov stupca da sortiraš · označi narudžbe pa "Napravi narudžbe" za bulk u Cash Schedule.', 'Click supplier card to filter · click column header to sort · check orders then "Create orders" for bulk Cash Schedule entries.')}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    {data.coverage_stats ? (
                        <span style={{ fontSize: '0.78rem', display: 'inline-flex', gap: 8 }}>
                            <span title={t('Stavke u potpunosti pokrivene zalihama (L0/L1/L2)', 'Items fully covered by stock (L0/L1/L2)')} style={{ color: '#1f7a36' }}>✓ {data.coverage_stats.orders_fully_covered}</span>
                            <span title={t('Djelomično pokrivene', 'Partially covered')} style={{ color: 'var(--ad-acc-3)' }}>◐ {data.coverage_stats.orders_partial}</span>
                            <span title={t('Nepokrivene — trebaju supplier order (L3)', 'Uncovered — need supplier order (L3)')} style={{ color: '#b14040' }}>✗ {data.coverage_stats.orders_uncovered}</span>
                        </span>
                    ) : null}
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', cursor: 'pointer' }} title={t('Skrij narudžbe koje imamo na stanju — fokus na ono što treba naručiti', 'Hide orders we have in stock — focus on what to order')}>
                        <input type="checkbox" checked={showUncoveredOnly} onChange={(e) => setShowUncoveredOnly(e.target.checked)} style={{ accentColor: '#b14040' }} />
                        {t('Samo nepokrivene', 'Uncovered only')}
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', cursor: 'pointer' }} title={t('Prikazuje samo narudžbe koje sadrže BOTH zlato i srebro', 'Show only orders containing BOTH gold and silver')}>
                        <input type="checkbox" checked={showMixedOnly} onChange={(e) => setShowMixedOnly(e.target.checked)} style={{ accentColor: '#1e40af' }} />
                        🥇🥈 {t('Samo miješane (Au+Ag)', 'Mixed only (Au+Ag)')}
                    </label>
                    {data.ignored_count != null && data.ignored_count > 0 ? (
                        <span className="muted" style={{ fontSize: '0.78rem' }} title={t('Označene narudžbe ostaju vidljive u listi (precrtane), samo se izuzimaju iz Preporuke', 'Ignored orders remain visible (struck through), excluded only from Recommendation')}>
                            ⚠ {data.ignored_count} {t('ignoriranih', 'ignored')}
                        </span>
                    ) : null}
                </div>
                {data.spot ? (
                    <span className="muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        💰 {t('Spot', 'Spot')} {formatDate(data.spot.spot_date, locale)}: <strong>{formatMoney(data.spot.price_eur_per_g, locale)}/g</strong> ({formatMoney(data.spot.price_eur_per_troy_oz, locale)}/oz)
                    </span>
                ) : null}
            </div>

            {/* Smart prijedlozi banner (heuristički) */}
            {(() => {
                if (!data?.by_order || !data?.by_supplier) return null;
                const hints: Array<{ icon: string; msg: string; color: string }> = [];
                // 1. Veliki supplier — bulk PO ako >25 stavki kod jednog supplier-a
                const topSupplier = [...data.by_supplier].sort((a, b) => b.items_count - a.items_count)[0];
                if (topSupplier && topSupplier.items_count >= 25) {
                    hints.push({ icon: '📌', msg: t(`${topSupplier.items_count} stavki za ${topSupplier.supplier} — bulk supplier order = bolja cijena`, `${topSupplier.items_count} items for ${topSupplier.supplier} — bulk supplier order = better pricing`), color: '#1e40af' });
                }
                // 2. Stare narudžbe bez paid_full
                const oldUnpaid = data.by_order.filter((o) => {
                    if (!o.item_date) return false;
                    const days = Math.floor((Date.now() - new Date(o.item_date).getTime()) / 86400000);
                    return days > 60 && (o.payment_status === 'unpaid' || o.payment_status === 'paid_partial');
                }).length;
                if (oldUnpaid >= 5) {
                    hints.push({ icon: '⚠', msg: t(`${oldUnpaid} narudžbi starijih 60d bez paid_full — razmisli isključiti iz plana`, `${oldUnpaid} orders >60d without paid_full — consider excluding from plan`), color: '#b14040' });
                }
                // 3. Coverage hint — ako >50% pokriveno, predloži skip L2
                if (data.coverage_stats && data.coverage_stats.orders_fully_covered) {
                    const total = (data.coverage_stats.orders_fully_covered + (data.coverage_stats.orders_partial ?? 0) + (data.coverage_stats.orders_uncovered ?? 0)) || 1;
                    const fullPct = Math.round(100 * data.coverage_stats.orders_fully_covered / total);
                    if (fullPct >= 60) hints.push({ icon: '💡', msg: t(`${fullPct}% narudžbi je već potpuno pokriveno lokalno — možeš isključiti L2 transfere u Plan & generate`, `${fullPct}% orders fully covered locally — you can disable L2 transfers in Plan & generate`), color: '#1f7a36' });
                }
                if (hints.length === 0) return null;
                return (
                    <div style={{ marginBottom: 12, padding: '10px 14px', background: 'linear-gradient(135deg, rgba(var(--ad-acc-rgb),0.08), rgba(107,79,28,0.04))', border: '1px solid rgba(var(--ad-acc-rgb),0.4)', borderRadius: 8 }}>
                        <strong style={{ fontSize: '0.78rem', color: 'var(--ad-ink)', display: 'block', marginBottom: 4 }}>💡 {t('Smart prijedlozi', 'Smart suggestions')}</strong>
                        {hints.map((h, i) => (
                            <div key={i} style={{ fontSize: '0.8rem', padding: '2px 0', color: h.color }}>
                                {h.icon} {h.msg}
                            </div>
                        ))}
                    </div>
                );
            })()}

            {data.premium_stats ? (() => {
                const s = data.premium_stats.sources;
                const total = (s.cost_layer ?? 0) + (s.cgm_product ?? 0) + (s.median_supplier_weight ?? 0) + (s.median_weight ?? 0) + (s.heuristic ?? 0);
                if (total === 0) return null;
                const pct = (n?: number) => Math.round(100 * (n ?? 0) / total);
                return (
                    <p className="muted" style={{ fontSize: '0.74rem', margin: '0 0 10px' }}>
                        🎯 {t('Izvor nabavne (RUC est.)', 'Cost source (GM est.)')}:
                        {pct(s.cost_layer) > 0 ? <> <strong style={{ color: '#1f7a36' }}>{pct(s.cost_layer)}% {t('cost layer (stvarni)', 'cost layer (real)')}</strong></> : null}
                        {pct(s.cgm_product) > 0 ? <> · <strong>{pct(s.cgm_product)}%</strong> {t('iz 40.1 Cijene', 'from 40.1 Prices')}</> : null}
                        {pct(s.median_supplier_weight) > 0 ? <> · {pct(s.median_supplier_weight)}% {t('median dobavljač+težina', 'median supplier+weight')}</> : null}
                        {pct(s.median_weight) > 0 ? <> · {pct(s.median_weight)}% {t('median težina', 'median weight')}</> : null}
                        {pct(s.heuristic) > 0 ? <> · <span style={{ color: '#b14040' }}>{pct(s.heuristic)}% {t('heuristika', 'heuristic')}</span></> : null}
                        {data.premium_stats.revision_id != null ? <> · {t('revizija', 'rev')} #{data.premium_stats.revision_id}</> : null}
                    </p>
                );
            })() : null}

            {/* Live preview KPI strip — agregat iz trenutnih filtriranih narudžbi */}
            {(() => {
                const totalOrders = filteredOrders.length;
                const totalItems = filteredOrders.reduce((s, o) => s + o.items_count, 0);
                const totalEur = filteredOrders.reduce((s, o) => s + o.total_eur, 0);
                const totalRuc = filteredOrders.reduce((s, o) => s + o.ruc_estimate_eur, 0);
                const fullCovered = filteredOrders.filter((o) => o.coverage === 'full').length;
                const partial = filteredOrders.filter((o) => o.coverage === 'partial').length;
                const uncovered = filteredOrders.filter((o) => o.coverage === 'none').length;
                const paidFullCnt = filteredOrders.filter((o) => o.payment_status === 'paid_full').length;
                return (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
                        <KpiMini label={t('Narudžbi', 'Orders')} value={String(totalOrders)} accent="var(--ad-ink)" />
                        <KpiMini label={t('Stavki', 'Items')} value={String(totalItems)} accent="var(--ad-ink)" />
                        <KpiMini label={t('Prodajno', 'Sales')} value={formatMoney(totalEur, locale) + ' €'} accent="#1f7a36" />
                        <KpiMini label={t('RUC est.', 'GM est.')} value={formatMoney(totalRuc, locale) + ' €'} accent={totalRuc >= 0 ? '#1f7a36' : '#b14040'} />
                        <KpiMini label={'✓ ' + t('Pokriveno', 'Covered')} value={`${fullCovered}/${totalOrders}`} accent="#1f7a36" sublabel={`${Math.round(100 * fullCovered / Math.max(1, totalOrders))}%`} />
                        <KpiMini label={'◐ ' + t('Djelomično', 'Partial')} value={String(partial)} accent="var(--ad-acc-3)" />
                        <KpiMini label={'✗ ' + t('Treba naručit', 'Need order')} value={String(uncovered)} accent="#b14040" />
                        <KpiMini label={'💰 ' + t('Paid full', 'Paid full')} value={`${paidFullCnt}/${totalOrders}`} accent="#1e40af" />
                    </div>
                );
            })()}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10, marginBottom: 16 }}>
                {data.by_supplier.map((s) => {
                    const active = activeSuppliers.has(s.supplier);
                    const marginColor = s.margin_pct >= 5 ? '#1f7a36' : s.margin_pct >= 0 ? 'var(--ad-acc-3)' : '#b14040';
                    const labelColor = active ? 'rgba(255,251,235,0.65)' : 'rgba(0,0,0,0.45)';
                    return (
                        <button
                            key={s.supplier}
                            type="button"
                            onClick={() => toggleSupplier(s.supplier)}
                            style={{
                                textAlign: 'left',
                                background: active ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : '#fff',
                                color: active ? '#fffbeb' : '#34383d',
                                border: active ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.3)',
                                borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                                boxShadow: active ? '0 6px 18px rgba(107,79,28,0.25)' : 'none',
                                transition: 'all 140ms ease',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                                <strong style={{ color: active ? '#fffbeb' : 'var(--ad-ink)', fontSize: '0.95rem' }}>
                                    {active ? '✓ ' : '🏭 '}{s.supplier}
                                </strong>
                                <span style={{ fontSize: '0.72rem', opacity: 0.75 }}>{s.items_count} {t('stavki', 'items')}</span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, fontSize: '0.78rem' }}>
                                <div>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: labelColor, marginBottom: 2 }}>{t('Prodajno', 'Sales')}</div>
                                    <strong style={{ fontSize: '0.86rem' }}>{formatMoney(s.total_eur, locale)}</strong>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: labelColor, marginBottom: 2 }}>{t('Nabavna est.', 'Cost est.')}</div>
                                    <strong style={{ fontSize: '0.86rem' }}>{formatMoney(s.cost_estimate_eur, locale)}</strong>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: labelColor, marginBottom: 2 }}>{t('RUC est.', 'GM est.')}</div>
                                    <strong style={{ fontSize: '0.86rem', color: active ? '#fffbeb' : marginColor }}>{formatMoney(s.ruc_estimate_eur, locale)}</strong>
                                    <div style={{ fontSize: '0.7rem', color: active ? 'rgba(255,251,235,0.85)' : marginColor, fontWeight: 600 }}>{s.margin_pct.toFixed(1)}%</div>
                                </div>
                            </div>

                            <div style={{ marginTop: 8, paddingTop: 6, borderTop: active ? '1px solid rgba(255,251,235,0.2)' : '1px solid rgba(var(--ad-soft-rgb),0.15)', fontSize: '0.76rem', display: 'flex', justifyContent: 'space-between' }}>
                                <span>🕘 {t('preostalo', 'remaining')}: <strong>{formatMoney(s.remaining_eur, locale)}</strong></span>
                                {s.oldest_date ? <span style={{ opacity: 0.75 }}>{formatDate(s.oldest_date, locale)}</span> : null}
                            </div>
                        </button>
                    );
                })}
            </div>

            {activeSuppliers.size > 0 ? (
                <div style={{ marginBottom: 10, fontSize: '0.82rem' }}>
                    <span className="muted">{t('Aktivni filter', 'Active filter')}: </span>
                    {Array.from(activeSuppliers).map((s) => (
                        <span key={s} style={{
                            display: 'inline-block', marginRight: 6, padding: '2px 8px', borderRadius: 6,
                            background: 'rgba(var(--ad-acc-rgb),0.18)', color: 'var(--ad-ink)', fontWeight: 600, fontSize: '0.78rem',
                        }}>{s} <span onClick={() => toggleSupplier(s)} style={{ cursor: 'pointer', marginLeft: 4 }}>✕</span></span>
                    ))}
                    <button type="button" className="secondary compact" onClick={() => { setActiveSuppliers(new Set()); setSelected(new Set()); }} style={{ fontSize: '0.72rem', padding: '2px 8px', marginLeft: 6 }}>
                        {t('Očisti', 'Clear')}
                    </button>
                </div>
            ) : null}

            {selected.size > 0 ? (
                <div style={{
                    background: 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.5) 100%)',
                    border: '1.5px solid var(--ad-acc)', borderRadius: 10, padding: '12px 16px', marginBottom: 12,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                        <strong style={{ color: 'var(--ad-ink)', fontSize: '0.92rem' }}>
                            ✓ {selected.size} {selected.size === 1 ? t('označena narudžba', 'order selected') : t('označenih narudžbi', 'orders selected')} · {selectedGroups.length} {selectedGroups.length === 1 ? t('dobavljač', 'supplier') : t('dobavljača', 'suppliers')}
                        </strong>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, fontSize: '0.84rem', alignItems: 'baseline' }}>
                            <span><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 4 }}>{t('Prodajno', 'Sales')}</span><strong>{formatMoney(selectedTotals.total, locale)}</strong></span>
                            <span><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 4 }}>{t('Nabavno', 'Cost')}</span><strong>{formatMoney(selectedTotals.cost, locale)}</strong></span>
                            <span><span className="muted" style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 4 }}>{t('RUC', 'GM')}</span><strong style={{ color: selectedTotals.ruc >= 0 ? '#1f7a36' : '#b14040' }}>{formatMoney(selectedTotals.ruc, locale)} <span style={{ fontSize: '0.74rem', fontWeight: 400 }}>({selectedTotals.margin_pct.toFixed(1)}%)</span></strong></span>
                        </div>
                        <button
                            type="button"
                            onClick={() => void createOrders()}
                            disabled={creating}
                            style={{
                                padding: '6px 14px', borderRadius: 8, border: 0,
                                background: creating ? '#888' : 'linear-gradient(135deg, #1f7a36, #2f9a4a)',
                                color: '#fff', fontWeight: 600, fontSize: '0.84rem', cursor: creating ? 'wait' : 'pointer',
                            }}
                        >
                            {creating ? '⏳ ' + t('Kreiram…', 'Creating…') : '📥 ' + t('Napravi narudžbe', 'Create orders')}
                        </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6, fontSize: '0.78rem' }}>
                        {selectedGroups.map((g) => (
                            <div key={g.supplier} style={{ background: '#fff', borderRadius: 6, padding: '6px 10px', border: '1px solid rgba(var(--ad-soft-rgb),0.25)' }}>
                                <strong style={{ color: 'var(--ad-ink)', fontSize: '0.8rem' }}>{g.supplier}</strong>
                                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>
                                    {t('Nabavno', 'Cost')} {formatMoney(g.cost_eur, locale)} → {t('RUC', 'GM')} <strong style={{ color: g.ruc_eur >= 0 ? '#1f7a36' : '#b14040' }}>{formatMoney(g.ruc_eur, locale)}</strong>
                                </div>
                                {/* Popis pripremljenih narudžbi: broj · poslovnica · datum (traženo od logistike). */}
                                <div style={{ marginTop: 4, borderTop: '1px dashed rgba(var(--ad-soft-rgb),0.25)', paddingTop: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {g.orders.map((o) => (
                                        <div key={o.order_number} style={{ fontSize: '0.68rem', color: '#555', display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                                            <span style={{ fontFamily: 'monospace' }}>{o.order_number}</span>
                                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--ad-ink)' }}>{o.branch ?? '—'}</span>
                                            <span className="muted">{o.item_date ? formatDate(o.item_date, locale) : '—'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {createError ? <p className="error" style={{ marginBottom: 10 }}>{createError}</p> : null}
            {createResult ? (
                <div style={{
                    background: 'rgba(31,122,54,0.08)', border: '1px solid rgba(31,122,54,0.3)',
                    borderRadius: 10, padding: '10px 14px', marginBottom: 12,
                }}>
                    <strong style={{ color: '#1f7a36', fontSize: '0.88rem' }}>
                        ✓ {t(`Kreirano ${createResult.length} narudžbi u Cash Schedule`, `Created ${createResult.length} orders in Cash Schedule`)}
                    </strong>
                    <ul style={{ margin: '6px 0 0 22px', fontSize: '0.82rem', color: '#34383d' }}>
                        {createResult.map((r) => (
                            <li key={r.id}>#{r.id} · {r.supplier_name} · {t('dospijeće', 'due')} {formatDate(r.due_date, locale)}</li>
                        ))}
                    </ul>
                    <button type="button" onClick={() => void sendAllPdfs()} style={{ marginTop: 8, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(31,122,54,0.4)', background: '#fff', color: '#1f7a36', fontSize: '0.78rem', cursor: 'pointer', fontWeight: 600 }}>
                        📧 {t('Pošalji sve PDF-ove dobavljačima', 'Send all PDFs to suppliers')}
                    </button>
                </div>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '12px 0 6px', flexWrap: 'wrap', gap: 8 }}>
                <h4 style={{ margin: 0, fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                    {t('Po narudžbi', 'By order')} <span className="muted" style={{ textTransform: 'none', letterSpacing: 0, marginLeft: 6, fontSize: '0.78rem' }}>· {filteredOrders.length} / {data.by_order?.length ?? 0}</span>
                </h4>
                <button
                    type="button"
                    onClick={toggleSelectAll}
                    disabled={filteredOrders.length === 0}
                    title={t('Označi / poništi sve prikazane narudžbe (poštuje aktivni filter dobavljača).', 'Select / clear all shown orders (respects active supplier filter).')}
                    style={{
                        padding: '4px 12px', borderRadius: 6,
                        border: '1px solid rgba(var(--ad-acc-rgb),0.5)', background: '#fff', color: 'var(--ad-ink)',
                        fontSize: '0.78rem', fontWeight: 600, cursor: filteredOrders.length === 0 ? 'default' : 'pointer',
                    }}
                >
                    {selected.size === filteredOrders.length && filteredOrders.length > 0 ? '☐ ' + t('Poništi sve', 'Clear all') : '☑ ' + t('Označi sve', 'Select all')}
                </button>
                <button
                    type="button"
                    onClick={() => void exportXlsx()}
                    disabled={exporting}
                    style={{
                        padding: '4px 12px', borderRadius: 6,
                        border: '1px solid rgba(31,122,54,0.4)', background: '#fff', color: '#1f7a36',
                        fontSize: '0.78rem', fontWeight: 600, cursor: exporting ? 'wait' : 'pointer',
                    }}
                >
                    {exporting ? '⏳ ' + t('Generiram…', 'Generating…') : '📊 ' + t('Export XLSX', 'Export XLSX')}
                </button>
            </div>
            <div className="table-wrap" style={{ marginBottom: 16 }}>
                <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                    <thead>
                        <tr>
                            <th style={{ width: 30, textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={filteredOrders.length > 0 && selected.size === filteredOrders.length}
                                    onChange={toggleSelectAll}
                                    style={{ accentColor: 'var(--ad-acc)', cursor: 'pointer' }}
                                />
                            </th>
                            <SortHeader k="date" label={t('Datum', 'Date')} />
                            <SortHeader k="order" label={t('Narudžba', 'Order')} />
                            <SortHeader k="partner" label={t('Partner', 'Partner')} />
                            <th>{t('Dobavljač(i)', 'Supplier(s)')}</th>
                            <th style={{ textAlign: 'center', whiteSpace: 'nowrap' }} title={t('Stock coverage: ✓ sve na stanju, ◐ djelomično, ✗ treba supplier', 'Stock coverage: ✓ all in stock, ◐ partial, ✗ needs supplier')}>{t('Pokriv.', 'Cover.')}</th>
                            <SortHeader k="total" label={t('Prodajno', 'Sales')} align="right" />
                            <SortHeader k="cost" label={t('Nabavno', 'Cost')} align="right" />
                            <SortHeader k="ruc" label={t('RUC', 'GM')} align="right" />
                            <SortHeader k="paid" label={t('Uplata', 'Paid')} align="right" />
                            <SortHeader k="remaining" label={t('Dug', 'Owed')} align="right" />
                            <SortHeader k="pct" label="%" align="right" />
                            <th style={{ textAlign: 'center', width: 60 }} title={t('Označi da se ova narudžba IGNORIRA u Preporuci. Ostaje vidljiva u listi (precrtana).', 'Mark order as IGNORED in Recommendation. Stays visible in list (struck through).')}>{t('Ignor.', 'Ignor.')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.slice(0, 500).map((o) => {
                            const isSelected = selected.has(o.order_number);
                            const rucColor = o.ruc_estimate_eur >= 0 ? '#1f7a36' : '#b14040';
                            return (
                                <tr
                                    key={o.order_number}
                                    style={{
                                        ...(isSelected ? { background: 'rgba(var(--ad-acc-rgb),0.08)' } : {}),
                                        ...(o.is_ignored ? { opacity: 0.45, textDecoration: 'line-through' } : {}),
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setDrawerOrder(o)}
                                    title={o.is_ignored ? t('Ignorirana narudžba (klikni za detalje)', 'Ignored order (click for details)') : t('Klikni za detalje narudžbe', 'Click for order details')}
                                >
                                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleSelect(o.order_number)}
                                            style={{ accentColor: 'var(--ad-acc)', cursor: 'pointer' }}
                                        />
                                    </td>
                                    <td className="muted" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{o.item_date ? formatDate(o.item_date, locale) : '—'}</td>
                                    <td style={{ fontSize: '0.78rem' }}>{o.order_number}</td>
                                    <td><PersonName value={o.partner_name} /></td>
                                    <td style={{ fontSize: '0.78rem' }}>
                                        {o.suppliers.map((s) => (
                                            <span key={s} style={{
                                                display: 'inline-block', marginRight: 4, marginBottom: 2,
                                                padding: '1px 6px', borderRadius: 4, fontSize: '0.72rem',
                                                background: s === 'Neraspoređeno' ? 'rgba(177,64,64,0.12)' : 'rgba(var(--ad-acc-rgb),0.15)',
                                                color: s === 'Neraspoređeno' ? '#b14040' : 'var(--ad-ink)',
                                                fontWeight: 600,
                                            }}>{s}</span>
                                        ))}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        {o.coverage === 'full' ? <span title={t('Sve stavke na stanju', 'All items in stock')} style={{ background: 'rgba(31,122,54,0.15)', color: '#1f7a36', padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700 }}>✓ {o.items_count}</span>
                                            : o.coverage === 'partial' ? <span title={t(`Pokrivenost: ${o.coverage_breakdown?.covered ?? 0}/${o.items_count} stavki na stanju`, `Coverage: ${o.coverage_breakdown?.covered ?? 0}/${o.items_count} items in stock`)} style={{ background: 'rgba(138,104,23,0.15)', color: 'var(--ad-acc-3)', padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700 }}>◐ {o.coverage_breakdown?.covered ?? 0}/{o.items_count}</span>
                                            : <span title={t('Nepokriveno — treba supplier order', 'Uncovered — needs supplier order')} style={{ background: 'rgba(177,64,64,0.15)', color: '#b14040', padding: '1px 6px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700 }}>✗ {o.items_count}</span>}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(o.total_eur, locale)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(o.cost_estimate_eur, locale)}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700, color: rucColor }}>
                                        {formatMoney(o.ruc_estimate_eur, locale)}
                                        <div style={{ fontSize: '0.68rem', fontWeight: 400, color: rucColor, opacity: 0.85 }}>{o.margin_pct.toFixed(1)}%</div>
                                    </td>
                                    <td style={{ textAlign: 'right', color: '#1f7a36' }}>{formatMoney(o.paid_eur, locale)}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700, color: o.remaining_eur > 0.01 ? '#b14040' : '#888' }}>{formatMoney(o.remaining_eur, locale)}</td>
                                    <td style={{ textAlign: 'right', fontSize: '0.76rem' }}>
                                        {o.payment_status === 'paid_full' ? (
                                            <span style={{ background: 'rgba(31,122,54,0.15)', color: '#1f7a36', padding: '2px 6px', borderRadius: 4, fontWeight: 600, fontSize: '0.7rem' }} title={t('Plaćeno u cijelosti — čeka isporuku', 'Paid in full — awaiting delivery')}>✓ 100%</span>
                                        ) : o.payment_status === 'paid_partial' ? (
                                            <span style={{ background: 'rgba(var(--ad-acc-rgb),0.15)', color: 'var(--ad-acc-3)', padding: '2px 6px', borderRadius: 4, fontWeight: 600, fontSize: '0.7rem' }} title={t('Djelomično plaćeno', 'Partially paid')}>{o.paid_pct.toFixed(0)}%</span>
                                        ) : (
                                            <span style={{ background: 'rgba(177,64,64,0.15)', color: '#b14040', padding: '2px 6px', borderRadius: 4, fontWeight: 600, fontSize: '0.7rem' }} title={t('Bez uplate', 'Unpaid')}>0%</span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                                            title={o.is_ignored ? t('Klik za vraćanje u Preporuku', 'Click to restore to Recommendation') : t('Klik da označiš kao ignorirano — ostaje u listi, ne ide u Preporuku', 'Click to mark as ignored — stays in list, excluded from Recommendation')}>
                                            <input
                                                type="checkbox"
                                                checked={!!o.is_ignored}
                                                onChange={() => void toggleIgnore(o.order_number)}
                                                style={{ accentColor: '#b14040', cursor: 'pointer' }}
                                            />
                                            {o.is_ignored ? <span style={{ fontSize: '0.66rem', color: '#b14040', fontWeight: 700 }}>IGNOR.</span> : null}
                                        </label>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {filteredOrders.length > 500 ? <p className="muted" style={{ fontSize: '0.78rem' }}>{t('Prikazano prvih 500 (od', 'Showing first 500 (of')} {filteredOrders.length}).</p> : null}
            </div>

            {/* Akcijski bar: Generiraj plan iz filtriranog/selectiranog skupa */}
            <div style={{
                position: 'sticky', bottom: 0, marginTop: 14,
                background: 'linear-gradient(135deg, rgba(var(--ad-acc-rgb),0.12), rgba(107,79,28,0.08))',
                border: '2px solid var(--ad-acc)', borderRadius: 8, padding: '12px 16px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
            }}>
                <div style={{ fontSize: '0.84rem' }}>
                    {selected.size > 0 ? (
                        <><strong>📌 {selected.size}</strong> {t('odabrano', 'selected')} · plan ide SAMO za odabrane</>
                    ) : (
                        <><strong>{filteredOrders.length}</strong> {t('narudžbi u filtru', 'orders in filter')}
                        {activeSuppliers.size > 0 ? ` · ${activeSuppliers.size} ${t('dobavljača aktivno', 'suppliers active')}` : ''}</>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => {
                        const orderNumbers = selected.size > 0
                            ? Array.from(selected)
                            : filteredOrders.map((o) => o.order_number);
                        try {
                            localStorage.setItem('bi.planner.plan_filters', JSON.stringify({
                                order_numbers: orderNumbers,
                                _ts: Date.now(),
                            }));
                        } catch { /* ignore */ }
                        onGoToPlan();
                    }}
                    style={{
                        background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))',
                        color: '#fffbeb', padding: '10px 18px', borderRadius: 8,
                        border: 'none', fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer',
                    }}
                >
                    🚚 {t('Generiraj plan iz filtriranog skupa', 'Generate plan from filtered set')} →
                </button>
            </div>

            {drawerOrder ? (
                <OrderDetailDrawer
                    order={drawerOrder}
                    items={data.items.filter((it) => it.order_number === drawerOrder.order_number)}
                    locale={locale}
                    onClose={() => setDrawerOrder(null)}
                />
            ) : null}
        </>
    );
}

function OrderDetailDrawer({ order, items, locale, onClose }: { order: BacklogOrder; items: BacklogItem[]; locale: Locale; onClose: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    return (
        <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div onClick={(e) => e.stopPropagation()} style={{
                width: 'min(820px, 96vw)', height: '100vh', overflowY: 'auto',
                background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                boxShadow: '-12px 0 40px rgba(107,79,28,0.22)',
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)',
                    padding: '14px 22px', color: '#fffbeb',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.72rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>{t('Narudžba', 'Order')}</p>
                        <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.15rem', fontWeight: 700 }}>{order.order_number}</h2>
                        <p style={{ margin: '4px 0 0', fontSize: '0.82rem', opacity: 0.9 }}>
                            <PersonName value={order.partner_name} /> · {order.item_date ? formatDate(order.item_date, locale) : '—'} · {order.branch ?? '—'}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 0, color: '#fff', fontSize: '1.5rem', cursor: 'pointer', padding: '4px 12px', borderRadius: 6 }}>✕</button>
                </div>

                <div style={{ padding: '14px 22px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 16 }}>
                        <div style={{ background: '#fff', borderRadius: 8, padding: '8px 12px', border: '1px solid rgba(var(--ad-soft-rgb),0.25)' }}>
                            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('Prodajno', 'Sales')}</div>
                            <strong style={{ fontSize: '0.95rem' }}>{formatMoney(order.total_eur, locale)}</strong>
                        </div>
                        <div style={{ background: '#fff', borderRadius: 8, padding: '8px 12px', border: '1px solid rgba(var(--ad-soft-rgb),0.25)' }}>
                            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('Nabavno est.', 'Cost est.')}</div>
                            <strong style={{ fontSize: '0.95rem' }}>{formatMoney(order.cost_estimate_eur, locale)}</strong>
                        </div>
                        <div style={{ background: '#fff', borderRadius: 8, padding: '8px 12px', border: '1px solid rgba(var(--ad-soft-rgb),0.25)' }}>
                            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('RUC est.', 'GM est.')}</div>
                            <strong style={{ fontSize: '0.95rem', color: order.ruc_estimate_eur >= 0 ? '#1f7a36' : '#b14040' }}>{formatMoney(order.ruc_estimate_eur, locale)} <span style={{ fontWeight: 400, fontSize: '0.78rem' }}>({order.margin_pct.toFixed(1)}%)</span></strong>
                        </div>
                        <div style={{ background: '#fff', borderRadius: 8, padding: '8px 12px', border: '1px solid rgba(var(--ad-soft-rgb),0.25)' }}>
                            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('Uplata / Dug', 'Paid / Owed')}</div>
                            <strong style={{ fontSize: '0.86rem', color: '#1f7a36' }}>{formatMoney(order.paid_eur, locale)}</strong> <span style={{ color: order.remaining_eur > 0.01 ? '#b14040' : '#888' }}>/ {formatMoney(order.remaining_eur, locale)}</span>
                        </div>
                    </div>

                    <h4 style={{ margin: '8px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                        {t('Artikli', 'Items')} · {items.length}
                    </h4>
                    <div className="table-wrap">
                        <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                            <thead>
                                <tr>
                                    <th>{t('Artikl', 'Item')}</th>
                                    <th>{t('Dobavljač', 'Supplier')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Kol.', 'Qty')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Fine', 'Fine')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Premium €/kom', 'Premium €/u')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Prodajno', 'Sales')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Nabavno', 'Cost')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('RUC', 'GM')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((it, i) => {
                                    const ruc = it.ruc_estimate_eur ?? 0;
                                    const sourceLabel = it.premium_source === 'cgm_product' ? '🎯' : it.premium_source === 'median_supplier_weight' ? '≈+' : it.premium_source === 'median_weight' ? '≈' : it.premium_source === 'heuristic' ? '~' : '';
                                    return (
                                        <tr key={i}>
                                            <td>{it.item_name}</td>
                                            <td><span style={{ fontWeight: 600, color: it.supplier === 'Neraspoređeno' ? '#b14040' : 'var(--ad-ink)' }}>{it.supplier}</span></td>
                                            <td style={{ textAlign: 'right' }}>{formatNumber(it.qty, locale)}</td>
                                            <td style={{ textAlign: 'right' }} className="muted">{it.fine_gold_g != null ? `${formatNumber(it.fine_gold_g, locale)} g` : '—'}</td>
                                            <td style={{ textAlign: 'right' }} title={t(`Izvor: ${it.premium_source ?? '—'}`, `Source: ${it.premium_source ?? '—'}`)}>{it.unit_premium_eur != null ? <>{formatMoney(it.unit_premium_eur, locale)} <span className="muted" style={{ fontSize: '0.72rem' }}>{sourceLabel}</span></> : '—'}</td>
                                            <td style={{ textAlign: 'right' }}>{formatMoney(it.total_eur, locale)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatMoney(it.cost_estimate_eur ?? 0, locale)}</td>
                                            <td style={{ textAlign: 'right', fontWeight: 700, color: ruc >= 0 ? '#1f7a36' : '#b14040' }}>{formatMoney(ruc, locale)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <p className="muted" style={{ fontSize: '0.7rem', marginTop: 8 }}>
                        🎯 = {t('iz 40.1 Cijene', 'from 40.1 Prices')} · ≈+ = {t('median dobavljač+težina', 'median supplier+weight')} · ≈ = {t('median težina', 'median weight')} · ~ = {t('heuristika', 'heuristic')}
                    </p>
                </div>
            </div>
        </div>
    );
}

/* ── Tab Recommendation ─────────────────────────────────────────── */

function RecommendationTab({ token, locale, state }: { token: string; locale: Locale; state: StateResponse }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [supplier, setSupplier] = useState('');
    const [budget, setBudget] = useState('');
    const [result, setResult] = useState<RecResponse | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [warehouses, setWarehouses] = useState<string[]>(state.visible_warehouses);
    const [committing, setCommitting] = useState(false);
    const [commitMsg, setCommitMsg] = useState('');

    async function commitOrder() {
        if (!result || result.recommendation.length === 0) return;
        const orderRef = window.prompt(t('Referenca narudžbe (opcionalno, npr. interni broj):', 'Order reference (optional, e.g. internal #):'), '');
        if (orderRef === null) return; // user cancel
        setCommitting(true);
        setCommitMsg('');
        try {
            const r = await mutate<{ id: number; due_date: string }>('/gold-cm-planner/cash-schedule', token, 'POST', {
                supplier_name: result.supplier,
                commitment_eur: result.total_cost_eur,
                committed_at: new Date().toISOString().slice(0, 10),
                order_reference: orderRef || null,
                recommendation_snapshot: result.recommendation,
            });
            setCommitMsg(t(`✓ Narudžba potvrđena (cash schedule #${r.id}, dospijeće ${r.due_date}). Otvori tab Cash schedule za pregled.`, `✓ Order confirmed (cash schedule #${r.id}, due ${r.due_date}). See Cash schedule tab.`));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setCommitting(false);
        }
    }

    const allWh = useMemo(() => state.all_warehouses, [state]);

    async function run() {
        if (!supplier || !budget) {
            setError(t('Odaberi dobavljača i unesi budget.', 'Pick a supplier and enter budget.'));
            return;
        }
        setBusy(true); setError(''); setResult(null);
        try {
            const qs = new URLSearchParams();
            qs.set('supplier', supplier);
            qs.set('budget_eur', budget);
            warehouses.forEach((w) => qs.append('warehouse_codes[]', w));
            setResult(await api<RecResponse>(`/gold-cm-planner/recommendation?${qs.toString()}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setBusy(false);
        }
    }

    const [saveMsg, setSaveMsg] = useState('');
    async function saveVisible() {
        setSaveMsg('');
        try {
            await mutate('/gold-cm-planner/visible-warehouses', token, 'PATCH', { warehouse_codes: warehouses });
            setError('');
            setSaveMsg(t(`✓ Spremljeno (${warehouses.length} skladišta).`, `✓ Saved (${warehouses.length} warehouses).`));
            setTimeout(() => setSaveMsg(''), 3000);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    function toggleWh(code: string) {
        setWarehouses((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
    }

    return (
        <>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.86rem' }}>
                {t(
                    'Odaberi dobavljača i raspoloživi budget. Algoritam će predložiti artikle (FIFO po starosti backloga) oduzevši stanje iz vidljivih skladišta.',
                    'Pick a supplier and available budget. Algorithm suggests items (FIFO by oldest backlog) minus stock in visible warehouses.',
                )}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 12 }}>
                <label className="filter-label" style={{ margin: 0 }}>
                    {t('Dobavljač', 'Supplier')}
                    <select value={supplier} onChange={(e) => setSupplier(e.target.value)}>
                        <option value="">— {t('Odaberi', 'Pick')} —</option>
                        {state.suppliers.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </label>
                <label className="filter-label" style={{ margin: 0 }}>
                    {t('Raspoloživi budget (EUR)', 'Available budget (EUR)')}
                    <input type="number" min="0" step="100" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="430000" />
                </label>
            </div>

            <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                    <strong style={{ fontSize: '0.86rem', color: '#34383d' }}>{t('Skladišta koja računam kao "imamo"', 'Warehouses counted as "we have"')}</strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {saveMsg ? <span style={{ color: '#1f7a36', fontSize: '0.76rem', fontWeight: 600 }}>{saveMsg}</span> : null}
                        <button type="button" className="secondary compact" onClick={() => void saveVisible()} style={{ fontSize: '0.76rem' }}>
                            💾 {t('Zapamti odabir', 'Remember selection')}
                        </button>
                    </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {allWh.map((w) => {
                        const on = warehouses.includes(w.code);
                        return (
                            <button
                                key={w.id}
                                type="button"
                                onClick={() => toggleWh(w.code)}
                                style={{
                                    padding: '3px 8px',
                                    borderRadius: 6,
                                    fontSize: '0.76rem',
                                    border: '1px solid ' + (on ? '#1f7a36' : 'rgba(var(--ad-soft-rgb),0.4)'),
                                    background: on ? 'rgba(31,122,54,0.13)' : '#fff',
                                    color: on ? '#1f7a36' : '#666',
                                    cursor: 'pointer',
                                    fontWeight: on ? 600 : 400,
                                }}
                            >
                                {on ? '✓ ' : ''}{w.code} · {w.name}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                <button type="button" className="compact" disabled={busy} onClick={() => void run()}>
                    {busy ? '⏳ ' + t('Računam…', 'Calculating…') : '🎯 ' + t('Predloži narudžbu', 'Propose order')}
                </button>
            </div>

            {error ? <p className="error">{error}</p> : null}
            {result?.message ? <p className="muted" style={{ fontStyle: 'italic' }}>{result.message}</p> : null}

            {result && result.recommendation.length > 0 ? (
                <RecommendationResult result={result} commitOrder={commitOrder} committing={committing} commitMsg={commitMsg} locale={locale} />
            ) : null}

            <MultiSupplierBatchPanel token={token} locale={locale} suppliers={state.suppliers} warehouses={warehouses} />
        </>
    );
}

/**
 * Multi-supplier batch order: slider/input per dobavljač + "Naruči sve" + hedge email trigger.
 * Iterira recommendation endpoint per supplier, prikaže consolidated tablicu, omogući batch place + hedge mail.
 */
function MultiSupplierBatchPanel({ token, locale, suppliers, warehouses }: { token: string; locale: Locale; suppliers: string[]; warehouses: string[] }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [budgets, setBudgets] = useState<Record<string, string>>({});
    const [results, setResults] = useState<Record<string, RecResponse>>({});
    const [busy, setBusy] = useState(false);
    const [placing, setPlacing] = useState(false);
    const [placeMsg, setPlaceMsg] = useState('');
    const [hedgeEnabled, setHedgeEnabled] = useState(true);
    const [hedgeEmail, setHedgeEmail] = useState('');
    const [hedgeNote, setHedgeNote] = useState('');
    const [priority, setPriority] = useState<'fifo' | 'max_orders'>('fifo');

    const activeSuppliers = useMemo(() => suppliers.filter((s) => {
        const b = parseFloat(budgets[s] ?? '0');
        return isFinite(b) && b > 0;
    }), [budgets, suppliers]);

    const runAll = useCallback(async () => {
        if (activeSuppliers.length === 0) return;
        setBusy(true); setResults({}); setPlaceMsg('');
        const next: Record<string, RecResponse> = {};
        for (const s of activeSuppliers) {
            const qs = new URLSearchParams();
            qs.set('supplier', s);
            qs.set('budget_eur', budgets[s] ?? '0');
            qs.set('priority', priority);
            warehouses.forEach((w) => qs.append('warehouse_codes[]', w));
            try {
                next[s] = await api<RecResponse>(`/gold-cm-planner/recommendation?${qs.toString()}`, token);
            } catch { /* skip */ }
        }
        setResults(next);
        setBusy(false);
    }, [activeSuppliers, budgets, warehouses, token, priority]);

    // Auto-refresh kad se budgets/warehouses/priority promijene (debounced 800ms da slider ne pucnja API).
    useEffect(() => {
        if (activeSuppliers.length === 0) { setResults({}); return; }
        const id = setTimeout(() => { void runAll(); }, 800);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(budgets), JSON.stringify(warehouses), priority]);

    const totals = useMemo(() => {
        let sales = 0, cost = 0, fineG = 0, itemCnt = 0;
        Object.values(results).forEach((r) => {
            sales += r.total_sales_eur ?? 0;
            cost += r.total_cost_eur ?? 0;
            r.recommendation.forEach((row: any) => {
                fineG += Number(row.fine_g_total ?? 0);
                itemCnt += 1;
            });
        });
        return { sales, cost, ruc: sales - cost, fineG, itemCnt };
    }, [results]);

    async function placeAll() {
        if (Object.keys(results).length === 0) return;
        if (!confirm(t(`Naručit ću ${Object.keys(results).length} dobavljača · ${totals.itemCnt} stavki · ${totals.cost.toFixed(0)} € nabave. Hedge mail: ${hedgeEnabled ? 'DA ('+totals.fineG.toFixed(1)+' g)' : 'NE'}. Nastaviti?`, `Will order from ${Object.keys(results).length} suppliers · ${totals.itemCnt} items · ${totals.cost.toFixed(0)} €. Hedge mail: ${hedgeEnabled ? 'YES ('+totals.fineG.toFixed(1)+' g)' : 'NO'}. Continue?`))) return;
        setPlacing(true); setPlaceMsg('');
        try {
            const ordersPayload = Object.values(results).map((r: any) => ({
                supplier_name: r.supplier,
                commitment_eur: Number(r.total_cost_eur ?? 0),
                expected_sales_eur: Number(r.total_sales_eur ?? 0),
                expected_cost_eur: Number(r.total_cost_eur ?? 0),
                expected_ruc_eur: Number(r.total_sales_eur ?? 0) - Number(r.total_cost_eur ?? 0),
                recommendation_snapshot: r.recommendation,
                notes: t('Generirano iz Multi-supplier batch panel', 'Generated from Multi-supplier batch panel'),
            }));
            const payload: any = { orders: ordersPayload };
            if (hedgeEnabled) {
                payload.hedge = {
                    send_email: true,
                    fine_g: totals.fineG,
                    note: hedgeNote || null,
                    to_email: hedgeEmail || null,
                };
            }
            const resp = await mutate<{ ok: boolean; created: any[]; hedge: { sent: boolean; to: string; message: string } }>('/gold-cm-planner/place-supplier-orders', token, 'POST', payload);
            setPlaceMsg(t(
                `✓ ${resp.created.length} narudžbi snimano · Hedge mail: ${resp.hedge.sent ? 'poslan na '+resp.hedge.to : (resp.hedge.message ?? '—')}`,
                `✓ ${resp.created.length} orders saved · Hedge mail: ${resp.hedge.sent ? 'sent to '+resp.hedge.to : (resp.hedge.message ?? '—')}`,
            ));
            // Reset
            setResults({});
            setBudgets({});
        } catch (e) {
            setPlaceMsg(t('Greška: ', 'Error: ') + (e instanceof Error ? e.message : 'API'));
        } finally {
            setPlacing(false);
        }
    }

    return (
        <div style={{ marginTop: 24, background: 'linear-gradient(135deg, rgba(var(--ad-acc-rgb),0.05), rgba(107,79,28,0.03))', border: '2px solid rgba(var(--ad-acc-rgb),0.4)', borderRadius: 12, padding: '16px 18px' }}>
            <h3 style={{ margin: '0 0 4px', color: 'var(--ad-ink)' }}>🎯 {t('Multi-supplier batch — slider per dobavljač + Naruči sve', 'Multi-supplier batch — slider per supplier + Order all')}</h3>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.78rem' }}>
                {t(
                    'Za svakog dobavljača unesi koliko želiš potrošiti. Auto-refresh dolje na promjenu slidera. Na "Naruči sve" ide hedge mail + per-supplier PDF/XLS narudžba.',
                    'For each supplier set budget. Auto-refresh below on slider change. "Order all" triggers hedge mail + per-supplier PDF/XLS.',
                )}
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', padding: '6px 10px', background: 'rgba(255,255,255,0.5)', borderRadius: 8 }}>
                <strong style={{ fontSize: '0.78rem', color: 'var(--ad-ink)' }}>{t('Prioritet algoritma:', 'Algorithm priority:')}</strong>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', cursor: 'pointer' }}>
                    <input type="radio" name="priority" checked={priority === 'fifo'} onChange={() => setPriority('fifo')} />
                    {t('FIFO (najstarije ponude prvo)', 'FIFO (oldest first)')}
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', cursor: 'pointer' }}>
                    <input type="radio" name="priority" checked={priority === 'max_orders'} onChange={() => setPriority('max_orders')} />
                    {t('Max. broj zatvorenih narudžbi unutar budžeta (jeftinije prvo)', 'Max orders within budget (cheaper first)')}
                </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10, marginBottom: 14 }}>
                {suppliers.map((s) => {
                    const v = budgets[s] ?? '';
                    const num = parseFloat(v);
                    const r = results[s];
                    return (
                        <div key={s} style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, padding: '10px 12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <strong style={{ fontSize: '0.84rem' }}>{s}</strong>
                                {r ? <span style={{ fontSize: '0.7rem', background: 'rgba(31,122,54,0.15)', color: '#1f7a36', padding: '2px 6px', borderRadius: 3, fontWeight: 700 }}>{r.recommendation.length} {t('stavki', 'items')}</span> : null}
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="2000000"
                                step="10000"
                                value={isFinite(num) ? num : 0}
                                onChange={(e) => setBudgets({ ...budgets, [s]: e.target.value })}
                                style={{ width: '100%' }}
                            />
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                                <input
                                    type="number"
                                    min="0"
                                    step="1000"
                                    value={v}
                                    onChange={(e) => setBudgets({ ...budgets, [s]: e.target.value })}
                                    placeholder="0"
                                    style={{ flex: 1, padding: '4px 6px', fontSize: '0.82rem' }}
                                />
                                <span className="muted" style={{ fontSize: '0.74rem' }}>€</span>
                            </div>
                            {r ? (
                                <div className="muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
                                    Σ {Number(r.total_cost_eur ?? 0).toFixed(0)} € cost · {Number(r.total_sales_eur ?? 0).toFixed(0)} € sale · RUC {(Number(r.total_sales_eur ?? 0) - Number(r.total_cost_eur ?? 0)).toFixed(0)} €
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <button type="button" disabled={busy || activeSuppliers.length === 0} onClick={() => void runAll()} style={{ background: 'var(--ad-ink)', color: '#fff', padding: '8px 16px', borderRadius: 6, border: 'none', fontWeight: 700 }}>
                    {busy ? '⏳ ' : '🎯 '}{t('Predloži za sve', 'Propose for all')} ({activeSuppliers.length})
                </button>
                {Object.keys(results).length > 0 ? (
                    <>
                        <span style={{ fontSize: '0.84rem', marginLeft: 8 }}>
                            Σ <strong>{totals.itemCnt}</strong> {t('stavki', 'items')} · <strong>{totals.cost.toFixed(0)} €</strong> nabave · RUC <strong style={{ color: totals.ruc >= 0 ? '#1f7a36' : '#b14040' }}>{totals.ruc.toFixed(0)} €</strong> · 🥇 <strong>{totals.fineG.toFixed(1)} g</strong>
                        </span>
                    </>
                ) : null}
            </div>

            {Object.keys(results).length > 0 ? (
                <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <label style={{ fontSize: '0.84rem' }}>
                            <input type="checkbox" checked={hedgeEnabled} onChange={(e) => setHedgeEnabled(e.target.checked)} />
                            🛡 {t('Pošalji hedge mail (otpusti', 'Send hedge mail (release')} <strong>{totals.fineG.toFixed(1)} g</strong>)
                        </label>
                        {hedgeEnabled ? (
                            <>
                                <input type="email" value={hedgeEmail} onChange={(e) => setHedgeEmail(e.target.value)} placeholder={t('hedging@... (opcionalno, default iz config)', 'hedging@... (optional, default from config)')} style={{ flex: 1, padding: '4px 8px', fontSize: '0.82rem' }} />
                                <input type="text" value={hedgeNote} onChange={(e) => setHedgeNote(e.target.value)} placeholder={t('napomena (opcionalno)', 'note (optional)')} style={{ flex: 1, padding: '4px 8px', fontSize: '0.82rem' }} />
                            </>
                        ) : null}
                    </div>
                    <button type="button" disabled={placing} onClick={() => void placeAll()} style={{ background: '#1f7a36', color: '#fff', padding: '10px 18px', borderRadius: 6, border: 'none', fontWeight: 700, fontSize: '0.92rem' }}>
                        {placing ? '⏳ ' : '✓ '}{t('Naruči sve + PDF/XLS po dobavljaču', 'Order all + PDF/XLS per supplier')}
                    </button>
                    {placeMsg ? <p style={{ marginTop: 8, color: placeMsg.startsWith('✓') ? '#1f7a36' : '#b14040', fontWeight: 600, fontSize: '0.84rem' }}>{placeMsg}</p> : null}
                </div>
            ) : null}
        </div>
    );
}

function RecommendationResult({ result, commitOrder, committing, commitMsg, locale }: {
    result: RecResponse;
    commitOrder: () => Promise<void>;
    committing: boolean;
    commitMsg: string;
    locale: Locale;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [expandedItem, setExpandedItem] = useState<string | null>(null);
    const totalSales = result.total_sales_eur ?? 0;
    const totalCost = result.total_cost_eur;
    const totalRuc = result.total_ruc_estimate_eur ?? (totalSales - totalCost);
    const margin = result.margin_pct ?? (totalSales > 0 ? (100 * totalRuc) / totalSales : 0);
    const rucColor = totalRuc >= 0 ? '#1f7a36' : '#b14040';
    const linkedOrders = result.linked_orders ?? [];

    return (
        <>
            {/* KPI header — Prodajno / Nabavno / RUC + Budget */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('Prodajno', 'Sales')}</div>
                    <strong style={{ fontSize: '1.15rem', color: '#34383d' }}>{formatMoney(totalSales, locale)}</strong>
                    <div className="muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>{t('Vezane narudžbe kupaca', 'Linked customer orders')}: {linkedOrders.length}</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('Nabavno est.', 'Cost est.')}</div>
                    <strong style={{ fontSize: '1.15rem', color: '#34383d' }}>{formatMoney(totalCost, locale)}</strong>
                    <div className="muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>{result.recommendation.length} {t('artikala', 'items')}</div>
                </div>
                <div style={{ background: 'linear-gradient(135deg, #fff, rgba(31,122,54,0.04))', border: '1px solid ' + (totalRuc >= 0 ? 'rgba(31,122,54,0.4)' : 'rgba(177,64,64,0.4)'), borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('RUC est.', 'GM est.')}</div>
                    <strong style={{ fontSize: '1.15rem', color: rucColor }}>{formatMoney(totalRuc, locale)}</strong>
                    <div style={{ fontSize: '0.78rem', marginTop: 2, color: rucColor, fontWeight: 600 }}>{margin.toFixed(1)}% {t('marže', 'margin')}</div>
                </div>
                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('Budget', 'Budget')}</div>
                    <strong style={{ fontSize: '1.0rem', color: '#34383d' }}>{formatMoney(result.budget_eur, locale)}</strong>
                    <div className="muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>{t('Ostaje', 'Left')}: {formatMoney(result.budget_remaining_eur, locale)}</div>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div className="muted" style={{ fontSize: '0.82rem' }}>
                    🎯 <strong>{t('Preporuka', 'Recommendation')}</strong> {t('za', 'for')} <strong>{result.supplier}</strong> · {result.recommendation.length} {t('artikala', 'items')}
                </div>
                <button
                    type="button"
                    onClick={() => void commitOrder()}
                    disabled={committing}
                    style={{
                        padding: '6px 16px', borderRadius: 8, border: 0,
                        background: committing ? '#888' : 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))',
                        color: '#fffbeb', fontWeight: 700, fontSize: '0.86rem', cursor: committing ? 'wait' : 'pointer',
                    }}
                >
                    {committing ? '⏳ ' + t('Potvrđujem…', 'Confirming…') : '✓ ' + t('Potvrdi narudžbu (cash schedule)', 'Confirm order (cash schedule)')}
                </button>
            </div>
            {commitMsg ? <p style={{ color: '#1f7a36', fontSize: '0.85rem', marginBottom: 12 }}>{commitMsg}</p> : null}

            {/* Per-artikl tablica s expand toggle za linked orders */}
            <h4 style={{ margin: '14px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                {t('Što naručiti', 'What to order')}
            </h4>
            <div className="table-wrap">
                <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                    <thead>
                        <tr>
                            <th style={{ width: 28 }}></th>
                            <th>{t('Artikl', 'Item')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Potreba', 'Needed')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Imamo', 'On hand')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Naručiti', 'Order')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Prodajno', 'Sales')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Nabavno', 'Cost')}</th>
                            <th style={{ textAlign: 'right' }}>{t('RUC', 'GM')}</th>
                            <th style={{ textAlign: 'right' }}>%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.recommendation.map((r) => {
                            const ruc = r.ruc_estimate_eur ?? 0;
                            const rucClr = ruc >= 0 ? '#1f7a36' : '#b14040';
                            const isExpanded = expandedItem === r.item_code;
                            const linkedN = r.linked_orders?.length ?? 0;
                            return (
                                <React.Fragment key={r.item_code}>
                                    <tr style={{ cursor: linkedN > 0 ? 'pointer' : 'default', background: isExpanded ? 'rgba(var(--ad-acc-rgb),0.06)' : undefined }} onClick={() => setExpandedItem(isExpanded ? null : r.item_code)}>
                                        <td style={{ textAlign: 'center', color: 'var(--ad-acc-3)' }}>{linkedN > 0 ? (isExpanded ? '▼' : '▶') : ''}</td>
                                        <td>
                                            <strong style={{ fontSize: '0.84rem' }}>{r.item_name}</strong>
                                            <div className="muted" style={{ fontSize: '0.72rem' }}>#{r.item_code} · {r.oldest_date ? `${t('najstariji', 'oldest')}: ${formatDate(r.oldest_date, locale)}` : ''}</div>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{formatNumber(r.qty_needed, locale)}</td>
                                        <td style={{ textAlign: 'right' }} className="muted">{formatNumber(r.qty_on_hand, locale)}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#1f7a36' }}>{formatNumber(r.qty_recommended, locale)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatMoney(r.total_sales_eur ?? 0, locale)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatMoney(r.total_cost_eur, locale)}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: rucClr }}>{formatMoney(ruc, locale)}</td>
                                        <td style={{ textAlign: 'right', color: rucClr, fontSize: '0.78rem' }}>{(r.margin_pct ?? 0).toFixed(1)}%</td>
                                    </tr>
                                    {isExpanded && r.linked_orders && r.linked_orders.length > 0 ? (
                                        <tr>
                                            <td colSpan={9} style={{ background: 'rgba(255,250,240,0.6)', padding: '8px 14px' }}>
                                                <div style={{ fontSize: '0.74rem', color: 'var(--ad-acc-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
                                                    {t('Zatvara ove narudžbe kupaca', 'Closes these customer orders')}
                                                </div>
                                                <table style={{ width: '100%', fontSize: '0.78rem' }}>
                                                    <thead>
                                                        <tr>
                                                            <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('Datum', 'Date')}</th>
                                                            <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('Narudžba', 'Order')}</th>
                                                            <th style={{ textAlign: 'left', padding: '4px 8px' }}>{t('Partner', 'Partner')}</th>
                                                            <th style={{ textAlign: 'right', padding: '4px 8px' }}>{t('Iskorišteno', 'Used')}/{t('Treba', 'Wants')}</th>
                                                            <th style={{ textAlign: 'right', padding: '4px 8px' }}>{t('Sale udio', 'Sale share')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {r.linked_orders.map((lo, j) => (
                                                            <tr key={j} style={{ borderTop: '1px solid rgba(var(--ad-soft-rgb),0.15)' }}>
                                                                <td style={{ padding: '4px 8px' }} className="muted">{lo.item_date ? formatDate(lo.item_date, locale) : '—'}</td>
                                                                <td style={{ padding: '4px 8px' }}>{lo.order_number}</td>
                                                                <td style={{ padding: '4px 8px' }}><PersonName value={lo.partner_name} /></td>
                                                                <td style={{ textAlign: 'right', padding: '4px 8px' }}>{formatNumber(lo.qty_used, locale)}/{formatNumber(lo.qty_total_for_item, locale)}</td>
                                                                <td style={{ textAlign: 'right', padding: '4px 8px' }}>{formatMoney(lo.sale_eur_share, locale)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Summary po narudžbi (deduplikat) */}
            {linkedOrders.length > 0 ? (
                <>
                    <h4 style={{ margin: '16px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                        {t('Vezane narudžbe kupaca (sažetak)', 'Linked customer orders (summary)')} · {linkedOrders.length}
                    </h4>
                    <div className="table-wrap">
                        <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                            <thead>
                                <tr>
                                    <th>{t('Datum', 'Date')}</th>
                                    <th>{t('Narudžba', 'Order')}</th>
                                    <th>{t('Partner', 'Partner')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Stavki', 'Items')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Kol.', 'Qty')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Sale udio', 'Sale share')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {linkedOrders.slice(0, 100).map((lo) => (
                                    <tr key={lo.order_number}>
                                        <td className="muted" style={{ whiteSpace: 'nowrap' }}>{lo.item_date ? formatDate(lo.item_date, locale) : '—'}</td>
                                        <td>{lo.order_number}</td>
                                        <td><PersonName value={lo.partner_name} /></td>
                                        <td style={{ textAlign: 'right' }}>{lo.items_count}</td>
                                        <td style={{ textAlign: 'right' }}>{formatNumber(lo.qty_total, locale)}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(lo.sale_eur, locale)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {linkedOrders.length > 100 ? <p className="muted" style={{ fontSize: '0.74rem' }}>{t('Prikazano prvih 100', 'Showing first 100')}</p> : null}
                    </div>
                </>
            ) : null}
        </>
    );
}

/* ── Tab Cash Schedule ─────────────────────────────────────────── */

function CashScheduleTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<CashResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusFilter, setStatusFilter] = useState<'open' | 'all' | 'paid'>('open');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            setData(await api<CashResponse>(`/gold-cm-planner/cash-schedule?status=${statusFilter}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, statusFilter]);

    useEffect(() => { void load(); }, [load]);

    async function markPaid(id: number, defaultEur: number) {
        const amount = window.prompt(t(`Iznos koji je plaćen (default ${defaultEur}):`, `Amount paid (default ${defaultEur}):`), String(defaultEur));
        if (amount === null) return;
        const paidEur = parseFloat(amount);
        if (!Number.isFinite(paidEur) || paidEur < 0) {
            alert(t('Neispravan iznos.', 'Invalid amount.'));
            return;
        }
        try {
            await mutate(`/gold-cm-planner/cash-schedule/${id}/mark-paid`, token, 'POST', { paid_eur: paidEur });
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    async function cancel(id: number, ref: string | null) {
        if (!confirm(t(`Stornirati narudžbu${ref ? ' (' + ref + ')' : ''}? Bit će obrisana iz cash schedule.`, `Cancel order${ref ? ' (' + ref + ')' : ''}? It will be removed from cash schedule.`))) return;
        try {
            await mutate(`/gold-cm-planner/cash-schedule/${id}`, token, 'DELETE');
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        }
    }

    if (loading && !data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    return (
        <>
            <p className="muted" style={{ fontSize: '0.84rem', margin: '0 0 12px' }}>
                {t(
                    'Cash schedule odvaja narudžbu (commitment, danas) od plaćanja (na dospijeću). Kreditni limiti i rokovi dolaze iz master data dobavljača (m.100 → 129).',
                    'Cash schedule separates ordering (commitment, today) from payment (at maturity). Limits and terms come from supplier master data (m.100 → 129).',
                )}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 16 }}>
                <SummaryMetric label={t('Ukupno otvoreno', 'Total open')} value={formatMoney(data.summary.total_open_eur, locale)} accent={data.summary.total_open_eur > 0 ? 'var(--ad-ink)' : '#888'} />
                <SummaryMetric label={t('Dospijeće 14 dana', 'Due within 14 days')} value={formatMoney(data.summary.due_in_14_days_eur, locale)} accent="var(--ad-acc-3)" />
                <SummaryMetric label={t('Past due (overdue)', 'Overdue')} value={formatMoney(data.summary.overdue_eur, locale)} accent={data.summary.overdue_eur > 0 ? '#b14040' : '#888'} />
            </div>

            <h3 style={{ margin: '12px 0 6px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                {t('Iskorištenost kreditnih limita', 'Credit limit usage')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10, marginBottom: 16 }}>
                {data.summary.by_supplier.map((s) => (
                    <div key={s.supplier_name} style={{
                        background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 14px',
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <strong style={{ color: 'var(--ad-ink)', fontSize: '0.92rem' }}>🏭 {s.supplier_name}</strong>
                            <span className="muted" style={{ fontSize: '0.78rem' }}>{s.open_count} {t('aktivnih', 'open')}</span>
                        </div>
                        <div style={{ marginTop: 6, fontSize: '0.82rem' }}>
                            <strong>{formatMoney(s.open_eur, locale)}</strong>
                            {s.credit_limit_eur != null ? <> / {formatMoney(s.credit_limit_eur, locale)}</> : <span className="muted"> · {t('bez limita', 'no limit')}</span>}
                        </div>
                        {s.credit_limit_eur != null && s.usage_pct != null ? (
                            <div style={{ marginTop: 6, height: 8, background: 'rgba(0,0,0,0.06)', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{
                                    width: `${Math.min(100, s.usage_pct)}%`,
                                    height: '100%',
                                    background: s.usage_pct > 90 ? '#b14040' : (s.usage_pct > 70 ? 'var(--ad-acc)' : '#1f7a36'),
                                }} />
                            </div>
                        ) : null}
                        {s.credit_limit_eur != null && s.usage_pct != null ? (
                            <div className="muted" style={{ fontSize: '0.74rem', marginTop: 4 }}>{s.usage_pct.toFixed(1)}% {t('iskorišteno', 'used')} · {t('rok', 'pay')} {s.default_days_to_pay}d</div>
                        ) : null}
                    </div>
                ))}
            </div>

            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {(['open', 'paid', 'all'] as const).map((s) => (
                    <button
                        key={s}
                        type="button"
                        onClick={() => setStatusFilter(s)}
                        style={{
                            padding: '4px 10px', borderRadius: 6,
                            border: statusFilter === s ? 0 : '1px solid rgba(var(--ad-soft-rgb),0.4)',
                            background: statusFilter === s ? 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))' : 'transparent',
                            color: statusFilter === s ? '#fffbeb' : 'var(--ad-ink)',
                            fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                        }}
                    >
                        {s === 'open' ? t('Otvoreno', 'Open') : s === 'paid' ? t('Plaćeno', 'Paid') : t('Sve', 'All')}
                    </button>
                ))}
            </div>

            {data.rows.length === 0 ? (
                <p className="muted" style={{ textAlign: 'center', padding: '1.5rem', fontStyle: 'italic' }}>
                    {t('Nema obveza s ovim statusom.', 'No entries with this status.')}
                </p>
            ) : (
                <div className="table-wrap">
                    <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                        <thead>
                            <tr>
                                <th>{t('Dospijeće', 'Due')}</th>
                                <th>{t('Dobavljač', 'Supplier')}</th>
                                <th>{t('Referenca', 'Reference')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Komitment', 'Commitment')}</th>
                                <th>{t('Status', 'Status')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r) => (
                                <tr key={r.id} style={r.is_overdue ? { background: 'rgba(177,64,64,0.05)' } : undefined}>
                                    <td>
                                        <strong>{formatDate(r.due_date, locale)}</strong>
                                        {r.is_overdue ? <div style={{ fontSize: '0.74rem', color: '#b14040', fontWeight: 700 }}>⚠ overdue</div> : (
                                            r.days_to_due != null && !r.paid_at ? <div className="muted" style={{ fontSize: '0.72rem' }}>{r.days_to_due > 0 ? `za ${Math.abs(r.days_to_due)}d` : 'danas'}</div> : null
                                        )}
                                    </td>
                                    <td>🏭 {r.supplier_name}</td>
                                    <td className="muted" style={{ fontSize: '0.8rem' }}>{r.order_reference ?? '—'}<br /><span style={{ fontSize: '0.72rem' }}>commit: {formatDate(r.committed_at, locale)}</span></td>
                                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(r.commitment_eur, locale)}</td>
                                    <td>
                                        {r.paid_at ? (
                                            <span style={{ background: 'rgba(31,122,54,0.15)', color: '#1f7a36', padding: '3px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>
                                                ✓ {t('Plaćeno', 'Paid')} {formatDate(r.paid_at, locale)}
                                                {r.paid_eur != null && r.paid_eur !== r.commitment_eur ? <span style={{ marginLeft: 4 }}>({formatMoney(r.paid_eur, locale)})</span> : null}
                                            </span>
                                        ) : r.is_overdue ? (
                                            <span style={{ background: 'rgba(177,64,64,0.15)', color: '#b14040', padding: '3px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>
                                                ⚠ overdue
                                            </span>
                                        ) : (
                                            <span style={{ background: 'rgba(var(--ad-acc-rgb),0.15)', color: 'var(--ad-acc-3)', padding: '3px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>
                                                ⏳ {t('Scheduled', 'Scheduled')}
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {!r.paid_at ? (
                                            <>
                                                <button type="button" className="compact" onClick={() => void markPaid(r.id, r.commitment_eur)} style={{ background: '#1f7a36', color: '#fff', fontSize: '0.74rem', padding: '3px 8px', marginRight: 4 }}>
                                                    ✓ {t('Plaćeno', 'Paid')}
                                                </button>
                                                <button type="button" className="secondary compact" onClick={() => void cancel(r.id, r.order_reference)} style={{ color: '#b14040', fontSize: '0.74rem', padding: '3px 8px' }}>
                                                    🗑
                                                </button>
                                            </>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

function SummaryMetric({ label, value, accent }: { label: string; value: string; accent: string }) {
    return (
        <div style={{
            background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 14px',
        }}>
            <div style={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{label}</div>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: accent, marginTop: 4 }}>{value}</div>
        </div>
    );
}

/* ── Tab Forecast ──────────────────────────────────────────────── */

function ForecastTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [days, setDays] = useState(30);
    const [threshold, setThreshold] = useState('0');
    const [data, setData] = useState<ForecastResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            setData(await api<ForecastResponse>(`/gold-cm-planner/forecast?days=${days}&alert_threshold_eur=${threshold || 0}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, days, threshold]);

    useEffect(() => { void load(); }, [load]);

    if (loading && !data) return <p className="muted">{t('Računam…', 'Calculating…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    // Pripremi sparkline path
    const minBal = Math.min(0, ...data.days.map((d) => d.ending_balance_eur));
    const maxBal = Math.max(...data.days.map((d) => d.ending_balance_eur), data.starting_balance_eur);
    const range = maxBal - minBal || 1;
    const W = 760;
    const H = 140;
    const points = data.days.map((d, i) => {
        const x = (i / Math.max(1, data.days.length - 1)) * W;
        const y = H - ((d.ending_balance_eur - minBal) / range) * H;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const polyline = points.join(' ');
    const zeroY = H - ((0 - minBal) / range) * H;

    return (
        <>
            <p className="muted" style={{ margin: '0 0 12px', fontSize: '0.84rem' }}>
                {t(
                    'Day-by-day projekcija likvidnosti. Trenutno stanje (iz bankovnih izvoda + opening balansi) + očekivani priljevi iz IRA-e (po due_date) - sigurni odljevi iz Cash Schedule.',
                    'Day-by-day liquidity projection. Current balance (from bank statements + opening) + expected inflows from outgoing invoices (by due_date) - certain outflows from Cash Schedule.',
                )}
            </p>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {t('Horizont', 'Horizon')}:
                    <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }}>
                        <option value="7">7d</option>
                        <option value="14">14d</option>
                        <option value="30">30d</option>
                        <option value="60">60d</option>
                        <option value="90">90d</option>
                    </select>
                </label>
                <label style={{ fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {t('Alarm threshold (EUR)', 'Alarm threshold (EUR)')}:
                    <input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', width: 100 }} />
                </label>
                <span className="muted" style={{ fontSize: '0.78rem', marginLeft: 'auto' }}>
                    {t('Start', 'Start')}: {data.starting_balance_as_of ? formatDate(data.starting_balance_as_of, locale) : '—'}
                </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 14 }}>
                <SummaryMetric label={t('Trenutni saldo', 'Starting balance')} value={formatMoney(data.starting_balance_eur, locale)} accent={data.starting_balance_eur < 0 ? '#b14040' : 'var(--ad-ink)'} />
                <SummaryMetric label={t('Očekivani priljevi', 'Expected inflows')} value={formatMoney(data.totals.inflows_expected_eur, locale)} accent="#1f7a36" />
                <SummaryMetric label={t('Sigurni odljevi', 'Certain outflows')} value={formatMoney(data.totals.outflows_scheduled_eur, locale)} accent="var(--ad-acc-3)" />
                <SummaryMetric label={t('Najniži saldo', 'Lowest balance')} value={formatMoney(data.totals.lowest_balance_eur, locale)} accent={data.totals.lowest_balance_eur < 0 ? '#b14040' : '#1f7a36'} />
            </div>

            {data.alerts.length > 0 ? (
                <div style={{ background: 'rgba(177,64,64,0.08)', border: '1px solid rgba(177,64,64,0.3)', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                    <strong style={{ color: '#b14040', fontSize: '0.86rem' }}>⚠ {data.alerts.length} {t('alarma likvidnosti', 'liquidity alerts')}</strong>
                    <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: '0.78rem', color: '#34383d' }}>
                        {data.alerts.slice(0, 5).map((a, i) => (
                            <li key={i}>{formatDate(a.date, locale)}: {a.message}</li>
                        ))}
                        {data.alerts.length > 5 ? <li className="muted">… +{data.alerts.length - 5}</li> : null}
                    </ul>
                </div>
            ) : null}

            <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: 12, marginBottom: 14, overflow: 'hidden' }}>
                <h3 style={{ margin: '0 0 8px', fontSize: '0.74rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>
                    {t('Linija salda (day-by-day)', 'Balance line (day-by-day)')}
                </h3>
                <svg viewBox={`0 0 ${W} ${H + 16}`} style={{ width: '100%', height: 160 }}>
                    {/* zero line */}
                    {zeroY >= 0 && zeroY <= H ? (
                        <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke="rgba(177,64,64,0.4)" strokeDasharray="4 4" />
                    ) : null}
                    {/* fill */}
                    <polyline points={polyline} fill="none" stroke="#c99b31" strokeWidth="2" />
                    {/* alert dots */}
                    {data.days.map((d, i) => {
                        if (!d.alert) return null;
                        const x = (i / Math.max(1, data.days.length - 1)) * W;
                        const y = H - ((d.ending_balance_eur - minBal) / range) * H;
                        return <circle key={i} cx={x} cy={y} r="3" fill={d.alert === 'negative' ? '#b14040' : 'var(--ad-acc)'} />;
                    })}
                </svg>
            </div>

            <details>
                <summary style={{ cursor: 'pointer', fontSize: '0.84rem', color: 'var(--ad-ink)', marginBottom: 8, fontWeight: 600 }}>
                    📅 {t('Detaljno po danima', 'Day-by-day detail')}
                </summary>
                <div className="table-wrap">
                    <table className="admin-user-table" style={{ fontSize: '0.82rem' }}>
                        <thead>
                            <tr>
                                <th>{t('Datum', 'Date')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Otvaranje', 'Opening')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Priljev (oček.)', 'Inflow (exp.)')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Odljev (sig.)', 'Outflow (cert.)')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Zatvaranje', 'Ending')}</th>
                                <th>{t('Alarm', 'Alert')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.days.map((d, i) => (
                                <tr key={i} style={d.alert ? { background: 'rgba(177,64,64,0.05)' } : undefined}>
                                    <td>{formatDate(d.date, locale)}</td>
                                    <td style={{ textAlign: 'right' }}>{formatMoney(d.opening_balance_eur, locale)}</td>
                                    <td style={{ textAlign: 'right', color: d.inflows_expected_eur > 0 ? '#1f7a36' : '#999' }}>{d.inflows_expected_eur > 0 ? '+' + formatMoney(d.inflows_expected_eur, locale) : '—'}</td>
                                    <td style={{ textAlign: 'right', color: d.outflows_scheduled_eur > 0 ? '#b14040' : '#999' }}>{d.outflows_scheduled_eur > 0 ? '-' + formatMoney(d.outflows_scheduled_eur, locale) : '—'}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 700, color: d.ending_balance_eur < 0 ? '#b14040' : '#34383d' }}>{formatMoney(d.ending_balance_eur, locale)}</td>
                                    <td>{d.alert === 'negative' ? <span style={{ color: '#b14040', fontWeight: 700 }}>⚠ negativan</span> : (d.alert === 'low' ? <span style={{ color: 'var(--ad-acc-3)' }}>⚠ nizak</span> : '')}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </details>
        </>
    );
}

/* ── Recent uploads log (ispod 3 file slot-a u Upload tabu) ───────── */

type RecentBatch = {
    id: number;
    import_type: string;
    source: string | null;
    filename: string | null;
    file_hash: string | null;
    status: string;
    rows_total: number;
    rows_committed: number;
    rows_skipped: number;
    error: string | null;
    started_at: string | null;
    completed_at: string | null;
    created_at: string;
    uploaded_by_name: string | null;
};

function RecentUploadsLog({ token, locale, reloadKey }: { token: string; locale: Locale; reloadKey: number }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<RecentBatch[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const r = await api<{ rows: RecentBatch[] }>('/gold-cm-planner/recent-uploads?limit=20', token);
            setRows(r.rows);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { void load(); }, [load, reloadKey]);

    const typeLabel = (tp: string): string => {
        switch (tp) {
            case 'gold_cm_open_orders': return t('Otvoreni predujmovi', 'Open orders');
            case 'gold_cm_confirmed_orders': return t('Potvrđene bez predujma (AD_PRO_ART)', 'Confirmed no-advance (AD_PRO_ART)');
            case 'gold_cm_stock': return t('Stanje zaliha', 'Stock');
            case 'gold_cm_supplier_map': return t('Popis dobavljača', 'Supplier map');
            case 'cash_register': return t('Stanje blagajne', 'Cash register');
            default: return tp;
        }
    };

    return (
        <div style={{ marginTop: 20, background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <strong style={{ color: 'var(--ad-ink)', fontSize: '0.9rem' }}>📜 {t('Prethodni uvozi (zadnjih 20)', 'Recent uploads (last 20)')}</strong>
                <button type="button" className="secondary compact" onClick={() => void load()} disabled={loading} style={{ fontSize: '0.78rem' }}>
                    {loading ? '⏳' : '🔄'} {t('Osvježi', 'Refresh')}
                </button>
            </div>
            <p className="muted" style={{ fontSize: '0.76rem', margin: '0 0 8px' }}>
                {t(
                    'Tracking iz bi_import_batches — uključuje i manualni upload (m.172) i obrada@ email auto-import. Klik na red u m.172 → drawer s detaljima i undo opcijom.',
                    'Tracking from bi_import_batches — includes manual upload (m.172) and obrada@ email auto-import. Click row in m.172 → detail drawer with undo.',
                )}
            </p>
            {error ? <p className="error">{error}</p> : null}
            {rows.length === 0 && !loading ? (
                <p className="muted" style={{ fontStyle: 'italic', textAlign: 'center', padding: '0.8rem', fontSize: '0.82rem' }}>
                    {t('Nema uvoza.', 'No imports yet.')}
                </p>
            ) : (
                <div className="table-wrap">
                    <table className="admin-user-table" style={{ fontSize: '0.8rem' }}>
                        <thead>
                            <tr>
                                <th>{t('Datum', 'Date')}</th>
                                <th>{t('Vrsta', 'Type')}</th>
                                <th>{t('Fajl', 'File')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Uneseno', 'Inserted')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Preskočeno', 'Skipped')}</th>
                                <th>{t('Status', 'Status')}</th>
                                <th>{t('Source', 'Src')}</th>
                                <th>{t('Korisnik', 'User')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((b) => (
                                <tr key={b.id}>
                                    <td>{formatDate(b.created_at.slice(0, 10), locale)}<div className="muted" style={{ fontSize: '0.7rem' }}>{b.created_at.slice(11, 19)}</div></td>
                                    <td><strong>{typeLabel(b.import_type)}</strong></td>
                                    <td title={b.filename ?? ''} style={{ fontSize: '0.76rem' }}>{(b.filename ?? '').slice(0, 30)}{(b.filename ?? '').length > 30 ? '…' : ''}</td>
                                    <td style={{ textAlign: 'right', color: '#1f7a36' }}>{Number(b.rows_committed).toLocaleString('hr-HR')}</td>
                                    <td style={{ textAlign: 'right', color: b.rows_skipped > 0 ? 'var(--ad-acc-3)' : '#999' }}>{b.rows_skipped > 0 ? Number(b.rows_skipped).toLocaleString('hr-HR') : '—'}</td>
                                    <td>
                                        <span style={{
                                            background: b.status === 'committed' ? 'rgba(31,122,54,0.15)' : (b.status === 'failed' ? 'rgba(177,64,64,0.15)' : 'rgba(var(--ad-acc-rgb),0.15)'),
                                            color: b.status === 'committed' ? '#1f7a36' : (b.status === 'failed' ? '#b14040' : 'var(--ad-acc-3)'),
                                            padding: '2px 7px', borderRadius: 5, fontSize: '0.72rem', fontWeight: 700,
                                        }}>{b.status}</span>
                                    </td>
                                    <td className="muted" style={{ fontSize: '0.74rem' }}>{b.source ?? '—'}</td>
                                    <td className="muted" style={{ fontSize: '0.74rem' }}>{b.uploaded_by_name ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="muted" style={{ fontSize: '0.72rem', marginTop: 8, fontStyle: 'italic' }}>
                {t('💡 Za potpun audit (uključujući druge tipove importa) otvori Administracija → Sustav → 172 Praćenje importa.', '💡 For full audit (including other import types) open Administration → System → 172 Import audit.')}
            </p>
        </div>
    );
}

/* ── Smart Ingest action row (multifile drop + fetch email + email log) ─
 *
 * Tri kartice u stilu m.172, ali filtrirano na m.40.1 (Gold CM Planer):
 *   1. Drop više fajlova → /admin/import-audit/upload (IngestClassifier auto-rutira).
 *   2. Dohvati email → /admin/import-audit/fetch-email (isti cron handler).
 *   3. Email log → /admin/import-audit/email-log?module_prefix=40.1.planner.
 */
type Gold40EmailLogRow = {
    id: number;
    sender_email: string | null;
    sender_name: string | null;
    subject: string | null;
    filename: string;
    classifier_guess: string | null;
    routed_to_module: string | null;
    status: string;
    import_status: string | null;
    imported_batch_id: number | null;
    created_at: string;
};

function SmartIngestActionRow({ token, locale, onReload }: { token: string; locale: Locale; onReload: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [uploadBusy, setUploadBusy] = useState(false);
    const [uploadOverlay, setUploadOverlay] = useState<ProgressOverlayState | null>(null);
    const [uploadResult, setUploadResult] = useState('');
    const [uploadIsError, setUploadIsError] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = React.useRef<HTMLInputElement | null>(null);
    const [fetchingEmail, setFetchingEmail] = useState(false);
    const [emailFetchResult, setEmailFetchResult] = useState<{ ok: boolean; output: string } | null>(null);
    const [emailLog, setEmailLog] = useState<Gold40EmailLogRow[]>([]);
    const [showEmailLog, setShowEmailLog] = useState(false);
    const [unknownWarehouses, setUnknownWarehouses] = useState<Array<{ code: string; name: string }>>([]);
    // Mail UID 820 (Vedran, 07.10.2026): novo skladiste (npr. Heimerle&Meile) vise ne smije tiho
    // dobiti ulogu "poslovnica" - coverage_role je sad obavezan izbor po skladistu ("POSTAVITI
    // SKLADISTE"), ne opcionalan checkbox kao prije.
    const [whSelections, setWhSelections] = useState<Record<string, { include: boolean; default_visible: boolean; coverage_role: string; notes: string }>>({});
    const COVERAGE_ROLES: Array<{ value: string; hr: string; en: string }> = [
        { value: 'poslovnica', hr: 'Poslovnica', en: 'Branch' },
        { value: 'veleprodaja', hr: 'Veleprodaja', en: 'Wholesale' },
        { value: 'akcijsko', hr: 'Akcijsko (centralno)', en: 'Promo (central)' },
        { value: 'samo_isporuke', hr: 'Samo za isporuke', en: 'Delivery only' },
    ];

    async function openWarehouses() {
        const included = unknownWarehouses.filter((w) => whSelections[w.code]?.include);
        const missingRole = included.filter((w) => !whSelections[w.code]?.coverage_role);
        if (missingRole.length > 0) {
            setUploadResult((prev) => `${prev}\n✗ ${t('Odaberi ulogu za svako novo skladiste prije otvaranja.', 'Pick a role for every new warehouse before opening.')}`);
            setUploadIsError(true);
            return;
        }
        const items = included.map((w) => ({
            code: w.code, name: w.name,
            is_default_visible: whSelections[w.code]?.default_visible ?? true,
            coverage_role: whSelections[w.code]?.coverage_role,
            notes: whSelections[w.code]?.notes || undefined,
        }));
        if (items.length === 0) {
            setUnknownWarehouses([]);
            onReload();
            return;
        }
        try {
            await mutate('/admin/gold-warehouses/bulk-open', token, 'POST', { items });
            setUnknownWarehouses([]);
            onReload();
        } catch (e) {
            setUploadResult((prev) => `${prev}\n✗ ${e instanceof Error ? e.message : 'bulk-open API error'}`);
            setUploadIsError(true);
        }
    }

    const loadEmailLog = useCallback(async () => {
        try {
            const r = await api<{ rows: Gold40EmailLogRow[]; total: number }>('/admin/import-audit/email-log?limit=30&module_prefix=40.1.planner', token);
            setEmailLog(r.rows);
        } catch {
            // tiho — log nije kritičan
        }
    }, [token]);

    useEffect(() => { void loadEmailLog(); }, [loadEmailLog]);

    async function doUploadOne(file: File): Promise<{ name: string; ok: boolean; message: string; unknownWarehouses?: Array<{ code: string; name: string }> }> {
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await uploadFile<{ message: string; import?: { unknown_warehouses?: Array<{ code: string; name: string }> } | null }>('/admin/import-audit/upload', token, fd);
            return { name: file.name, ok: true, message: r.message, unknownWarehouses: r.import?.unknown_warehouses };
        } catch (e) {
            return { name: file.name, ok: false, message: e instanceof Error ? e.message : String(e) };
        }
    }

    async function doUploadMany(files: FileList | File[]) {
        const list = Array.from(files);
        if (list.length === 0) return;
        setUploadBusy(true); setUploadResult(''); setUploadIsError(false); setUnknownWarehouses([]);
        setUploadOverlay({
            label: t(`Upload ${list.length} fajlova`, `Upload ${list.length} files`),
            progress: 0, phase: 'preparing', message: t('Pripremam…', 'Preparing…'),
        });
        const results: { name: string; ok: boolean; message: string; unknownWarehouses?: Array<{ code: string; name: string }> }[] = [];
        for (let i = 0; i < list.length; i++) {
            const f = list[i];
            setUploadOverlay({
                label: t(`Upload ${i + 1}/${list.length}: ${f.name}`, `Upload ${i + 1}/${list.length}: ${f.name}`),
                progress: Math.round((i / list.length) * 100),
                phase: 'downloading',
                message: t('Šaljem na server + klasifikator…', 'Uploading + classifier…'),
            });
            setUploadResult(t(`Obrađujem ${i + 1}/${list.length}: ${f.name}…`, `Processing ${i + 1}/${list.length}: ${f.name}…`));
            const r = await doUploadOne(f);
            results.push(r);
            setUploadOverlay((s) => s ? { ...s, progress: Math.round(((i + 1) / list.length) * 100), message: r.ok ? t('✓ Obrađeno', '✓ Done') : t('✗ Greška', '✗ Error') } : s);
        }
        const okCount = results.filter((r) => r.ok).length;
        const failCount = results.length - okCount;
        const summary = t(`${okCount}/${results.length} OK${failCount > 0 ? `, ${failCount} grešaka` : ''}`, `${okCount}/${results.length} OK${failCount > 0 ? `, ${failCount} errors` : ''}`);
        const details = results.map((r) => `${r.ok ? '✓' : '✗'} ${r.name}: ${r.message}`).join('\n');
        setUploadResult(`${summary}\n\n${details}`);
        setUploadIsError(failCount > 0);
        setUploadOverlay({ label: summary, progress: 100, phase: failCount > 0 ? 'error' : 'done', message: failCount > 0 ? t('Sa greškama — vidi detalje.', 'With errors — see details.') : t('Sve OK ✓', 'All OK ✓') });
        setTimeout(() => setUploadOverlay(null), failCount > 0 ? 2500 : 1200);
        setUploadBusy(false);

        const allUnknown: Record<string, { code: string; name: string }> = {};
        results.forEach((r) => (r.unknownWarehouses ?? []).forEach((w) => { allUnknown[w.code] = w; }));
        const uniqUnknown = Object.values(allUnknown);
        if (uniqUnknown.length > 0) {
            setUnknownWarehouses(uniqUnknown);
            const initial: Record<string, { include: boolean; default_visible: boolean; coverage_role: string; notes: string }> = {};
            uniqUnknown.forEach((w) => { initial[w.code] = { include: true, default_visible: true, coverage_role: '', notes: '' }; });
            setWhSelections(initial);
        }

        await loadEmailLog();
        onReload();
    }

    async function fetchEmail() {
        setFetchingEmail(true);
        setEmailFetchResult(null);
        try {
            const r = await api<{ ok: boolean; output: string }>('/admin/import-audit/fetch-email', token, { method: 'POST' });
            setEmailFetchResult({ ok: r.ok, output: r.output });
            await loadEmailLog();
            onReload();
        } catch (e) {
            setEmailFetchResult({ ok: false, output: e instanceof Error ? e.message : 'fail' });
        } finally {
            setFetchingEmail(false);
        }
    }

    return (
        <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 14 }}>
                {/* Smart multifile upload */}
                <button
                    type="button"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); const fs = e.dataTransfer.files; if (fs && fs.length > 0) void doUploadMany(fs); }}
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadBusy}
                    style={{
                        textAlign: 'left',
                        background: dragOver
                            ? 'rgba(var(--ad-acc-rgb),0.10)'
                            : 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)',
                        border: dragOver ? '2.5px dashed var(--ad-acc)' : '2.5px dashed rgba(var(--ad-soft-rgb),0.45)',
                        borderRadius: 14,
                        padding: '1rem 1.2rem',
                        cursor: uploadBusy ? 'wait' : 'pointer',
                        transition: 'all 160ms ease',
                        opacity: uploadBusy ? 0.7 : 1,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 6 }}>
                        <span style={{ fontSize: '1.7rem' }}>{uploadBusy ? '⏳' : '📤'}</span>
                        <strong style={{ color: 'var(--ad-ink)', fontSize: '0.96rem' }}>
                            {uploadBusy ? t('Obrađujem…', 'Processing…') : (dragOver ? t('Otpusti ovdje', 'Drop here') : t('Smart upload (povuci ili klikni)', 'Smart upload (drop or click)'))}
                        </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.4 }}>
                        {t(
                            'Jedan ili više Faros xlsx-a odjednom. IngestClassifier ih auto-rutira (otvoreni predujmovi · potvrđene bez-predujma AD_PRO_ART · zalihe · dobavljači).',
                            'One or many Faros xlsx files at once. IngestClassifier auto-routes (open advances · confirmed no-advance AD_PRO_ART · stock · suppliers).',
                        )}
                    </p>
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        style={{ display: 'none' }}
                        onChange={(e) => { const fs = e.target.files; if (fs && fs.length > 0) void doUploadMany(fs); e.currentTarget.value = ''; }}
                    />
                </button>

                {/* Dohvati email */}
                <button
                    type="button"
                    disabled={fetchingEmail}
                    onClick={() => void fetchEmail()}
                    style={{
                        textAlign: 'left',
                        background: fetchingEmail
                            ? 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
                            : 'linear-gradient(135deg, #eff6ff 0%, rgba(219,234,254,0.55) 100%)',
                        border: '1.5px solid rgba(59,130,246,0.28)',
                        borderRadius: 14,
                        padding: '1rem 1.2rem',
                        cursor: fetchingEmail ? 'wait' : 'pointer',
                        transition: 'all 160ms ease',
                        opacity: fetchingEmail ? 0.7 : 1,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 6 }}>
                        <span style={{ fontSize: '1.7rem' }}>{fetchingEmail ? '⏳' : '📧'}</span>
                        <strong style={{ color: '#1e40af', fontSize: '0.96rem' }}>
                            {fetchingEmail ? t('Dohvaćam…', 'Fetching…') : t('Dohvati email (obrada@)', 'Fetch email (obrada@)')}
                        </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.4 }}>
                        {t('Ručno povuci nove privitke iz obrada@aurodomus.eu. Cron radi svaku minutu.', 'Manually pull attachments from obrada@aurodomus.eu. Cron runs every minute.')}
                    </p>
                </button>

                {/* Email log toggle (samo 40.1.planner) */}
                <button
                    type="button"
                    onClick={() => setShowEmailLog((v) => !v)}
                    style={{
                        textAlign: 'left',
                        background: showEmailLog
                            ? 'linear-gradient(135deg, #f0fdf4 0%, rgba(220,252,231,0.55) 100%)'
                            : 'linear-gradient(135deg, #f8fafc 0%, rgba(241,245,249,0.55) 100%)',
                        border: showEmailLog ? '1.5px solid rgba(31,122,54,0.35)' : '1.5px solid rgba(148,163,184,0.32)',
                        borderRadius: 14,
                        padding: '1rem 1.2rem',
                        cursor: 'pointer',
                        transition: 'all 160ms ease',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 6 }}>
                        <span style={{ fontSize: '1.7rem' }}>📜</span>
                        <strong style={{ color: showEmailLog ? '#1f7a36' : '#334155', fontSize: '0.96rem', flex: 1 }}>
                            {t('Email log (40.1)', 'Email log (40.1)')}
                        </strong>
                        <span style={{
                            background: showEmailLog ? '#1f7a36' : '#94a3b8',
                            color: '#fff', fontSize: '0.72rem', fontWeight: 700,
                            padding: '2px 8px', borderRadius: 10, minWidth: 26, textAlign: 'center',
                        }}>{emailLog.length}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.4 }}>
                        {t('Samo fajlovi koji se tiču m.40.1 Planera (Faros: otvoreni predujmovi · zalihe · dobavljači).', 'Only files relevant to m.40.1 Planner (Faros: open advances · stock · suppliers).')}
                    </p>
                </button>
            </div>

            {uploadResult ? (
                <div style={{
                    padding: '10px 14px', marginBottom: 12, borderRadius: 10,
                    background: uploadIsError ? 'rgba(177,64,64,0.08)' : 'rgba(31,122,54,0.08)',
                    border: uploadIsError ? '1px solid rgba(177,64,64,0.3)' : '1px solid rgba(31,122,54,0.3)',
                    fontSize: '0.84rem',
                    color: uploadIsError ? '#b14040' : '#1f7a36',
                    whiteSpace: 'pre-wrap',
                    fontFamily: uploadResult.includes('\n') ? 'ui-monospace, SFMono-Regular, monospace' : undefined,
                }}>{uploadResult}</div>
            ) : null}

            {emailFetchResult ? (
                <div style={{
                    padding: '10px 14px', marginBottom: 12, borderRadius: 10,
                    background: emailFetchResult.ok ? 'rgba(31,122,54,0.08)' : 'rgba(177,64,64,0.08)',
                    border: emailFetchResult.ok ? '1px solid rgba(31,122,54,0.3)' : '1px solid rgba(177,64,64,0.3)',
                    fontSize: '0.82rem', whiteSpace: 'pre-wrap',
                }}>
                    <strong>{emailFetchResult.ok ? '✓ ' : '✗ '}{emailFetchResult.ok ? t('Dohvat OK', 'Fetch OK') : t('Greška', 'Error')}</strong>
                    {emailFetchResult.output ? <div style={{ marginTop: 4, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: '0.74rem' }}>{emailFetchResult.output.slice(0, 800)}</div> : null}
                </div>
            ) : null}

            {showEmailLog ? (
                <div style={{
                    marginBottom: 14, padding: '10px 12px',
                    background: 'rgba(31,122,54,0.04)',
                    border: '1px solid rgba(31,122,54,0.18)', borderRadius: 10,
                }}>
                    {emailLog.length === 0 ? (
                        <p className="muted" style={{ margin: 0, fontSize: '0.82rem', fontStyle: 'italic' }}>
                            {t('Još nema 40.1 fajlova u ingest pipeline-u.', 'No 40.1 files in ingest pipeline yet.')}
                        </p>
                    ) : (
                        <div className="table-wrap">
                            <table className="admin-user-table" style={{ fontSize: '0.78rem' }}>
                                <thead>
                                    <tr>
                                        <th>{t('Vrijeme', 'Time')}</th>
                                        <th>{t('Pošiljatelj', 'Sender')}</th>
                                        <th>{t('Fajl', 'File')}</th>
                                        <th>{t('Modul', 'Module')}</th>
                                        <th>{t('Status', 'Status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {emailLog.map((r) => (
                                        <tr key={r.id}>
                                            <td className="muted" style={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{r.created_at.replace('T', ' ').slice(0, 16)}</td>
                                            <td>{r.sender_email ?? <span className="muted">—</span>}</td>
                                            <td style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: '0.74rem' }}>{r.filename}</td>
                                            <td className="muted" style={{ fontSize: '0.72rem' }}>{r.routed_to_module ?? r.classifier_guess ?? '—'}</td>
                                            <td>
                                                {r.import_status === 'success' ? <span style={{ color: '#1f7a36', fontWeight: 700 }}>✓ {t('uvezeno', 'imported')}</span>
                                                    : r.import_status === 'failed' ? <span style={{ color: '#b14040', fontWeight: 700 }}>✗ {t('greška', 'failed')}</span>
                                                    : <span className="muted">{r.status}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : null}

            {unknownWarehouses.length > 0 ? (
                <div className="modal-backdrop" role="dialog" aria-modal="true">
                    <div onClick={(e) => e.stopPropagation()} style={{
                        width: 'min(640px, 96vw)', maxHeight: '88vh', overflowY: 'auto',
                        background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                        borderRadius: 16,
                        boxShadow: '0 20px 60px rgba(107,79,28,0.28), 0 0 0 1px rgba(var(--ad-soft-rgb),0.18)',
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)',
                            padding: '1rem 1.4rem', color: '#fffbeb',
                        }}>
            <p style={{ margin: 0, fontSize: '0.72rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>
                                {t('POSTAVITI SKLADIŠTE', 'SET UP WAREHOUSE')}
                            </p>
                            <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.25rem', fontWeight: 700 }}>
                                {t(`Pronašli smo ${unknownWarehouses.length} novih skladišta`, `Found ${unknownWarehouses.length} new warehouses`)}
                            </h2>
                        </div>
                        <div style={{ padding: '16px 22px' }}>
                            <p className="muted" style={{ fontSize: '0.86rem', margin: '0 0 14px' }}>
                                {t('Mail UID 820: novo skladište se više ne smije tiho tretirati kao poslovnica. Za svako odaberi stvarnu ulogu prije otvaranja - od toga ovisi hoće li njegova zaliha ući u S1/S2/S4 u 40.61.', 'UID 820: a new warehouse can no longer silently default to "branch". Pick its real role for every one before opening - this decides whether its stock enters S1/S2/S4 in 40.61.')}
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {unknownWarehouses.map((w) => {
                                    const sel = whSelections[w.code] ?? { include: true, default_visible: true, coverage_role: '', notes: '' };
                                    const roleMissing = sel.include && !sel.coverage_role;
                                    return (
                                        <div key={w.code} style={{
                                            padding: '10px 12px',
                                            border: roleMissing ? '1px solid #b14040' : '1px solid rgba(var(--ad-soft-rgb),0.25)',
                                            borderRadius: 10,
                                            background: sel.include ? 'rgba(var(--ad-acc-rgb),0.08)' : '#fff',
                                            display: 'flex', flexDirection: 'column', gap: 8,
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1 }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={sel.include}
                                                        onChange={(e) => setWhSelections({ ...whSelections, [w.code]: { ...sel, include: e.target.checked } })}
                                                        style={{ accentColor: 'var(--ad-acc)' }}
                                                    />
                                                    <div>
                                                        <strong style={{ fontSize: '0.92rem', color: '#34383d' }}>{w.code}</strong>
                                                        <span className="muted" style={{ marginLeft: 8 }}>{w.name}</span>
                                                    </div>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.78rem' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={sel.default_visible}
                                                        onChange={(e) => setWhSelections({ ...whSelections, [w.code]: { ...sel, default_visible: e.target.checked } })}
                                                        style={{ accentColor: 'var(--ad-acc)' }}
                                                        disabled={!sel.include}
                                                    />
                                                    <span>{t('Default vidljivo', 'Default visible')}</span>
                                                </label>
                                            </div>
                                            {sel.include ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingLeft: 26 }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem' }}>
                                                        {t('Uloga *', 'Role *')}
                                                        <select
                                                            value={sel.coverage_role}
                                                            onChange={(e) => setWhSelections({ ...whSelections, [w.code]: { ...sel, coverage_role: e.target.value } })}
                                                            style={{ padding: '3px 6px', border: roleMissing ? '1px solid #b14040' : undefined }}
                                                        >
                                                            <option value="">{t('— odaberi —', '— pick —')}</option>
                                                            {COVERAGE_ROLES.map((r) => <option key={r.value} value={r.value}>{locale === 'en' ? r.en : r.hr}</option>)}
                                                        </select>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={sel.notes}
                                                        onChange={(e) => setWhSelections({ ...whSelections, [w.code]: { ...sel, notes: e.target.value } })}
                                                        placeholder={t('napomena, npr. ime komisionara', 'note, e.g. consignee name')}
                                                        style={{ padding: '3px 6px', fontSize: '0.78rem', flex: 1, minWidth: 160 }}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, borderTop: '1px solid rgba(var(--ad-soft-rgb),0.2)', paddingTop: 14 }}>
                                <button type="button" className="secondary compact" onClick={() => { setUnknownWarehouses([]); onReload(); }}>
                                    {t('Preskoči', 'Skip')}
                                </button>
                                <button
                                    type="button"
                                    className="compact"
                                    disabled={unknownWarehouses.some((w) => (whSelections[w.code] ?? { include: true }).include && !whSelections[w.code]?.coverage_role)}
                                    onClick={() => void openWarehouses()}
                                >
                                    ✓ {t('Otvori odabrana', 'Open selected')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
            <ProgressOverlay state={uploadOverlay} />
        </>
    );
}

/**
 * Branch Reserved Stock — poslovnica označi artikle koji su FIZIČKI kod njih za narudžbu kupca.
 * Engine smanjuje L0/L2 free stock pool za rezervirano.
 */
function BranchReservedTab({ token, locale, state }: { token: string; locale: Locale; state: StateResponse }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [whCode, setWhCode] = useState('');
    const [reserved, setReserved] = useState<any[]>([]);
    const [eligible, setEligible] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [addForm, setAddForm] = useState<{ order_number: string; item_code: string; qty: string; partner_name: string; note: string } | null>(null);

    const loadReserved = useCallback(async () => {
        if (!whCode) { setReserved([]); setEligible([]); return; }
        setLoading(true);
        try {
            const [r, e] = await Promise.all([
                api<{ rows: any[] }>(`/gold-cm-planner/branch-reserved?warehouse_code=${encodeURIComponent(whCode)}`, token),
                api<{ orders: any[] }>(`/gold-cm-planner/branch-reserved/eligible?warehouse_code=${encodeURIComponent(whCode)}`, token),
            ]);
            setReserved(r.rows ?? []);
            setEligible(e.orders ?? []);
        } catch { /* */ }
        finally { setLoading(false); }
    }, [whCode, token]);
    useEffect(() => { void loadReserved(); }, [loadReserved]);

    async function release(id: number) {
        if (!confirm(t('Otpusti rezervaciju? Roba se vraća u free stock pool.', 'Release reservation? Stock returns to free pool.'))) return;
        // 404 (vec otpustena) i 403 (tudja poslovnica) javljaju poruku i osvjezavaju popis (nezavisni audit n2, nalaz 6).
        try {
            await mutate(`/gold-cm-planner/branch-reserved/${id}`, token, 'DELETE');
        } catch (e) {
            alert(e instanceof Error ? e.message : t('Greška', 'Error'));
        } finally {
            await loadReserved();
        }
    }
    async function addReserved() {
        if (!addForm || !whCode) return;
        try {
            await mutate('/gold-cm-planner/branch-reserved', token, 'POST', {
                warehouse_code: whCode,
                item_code: parseInt(addForm.item_code),
                qty: parseFloat(addForm.qty),
                order_number: addForm.order_number,
                partner_name: addForm.partner_name || null,
                note: addForm.note || null,
            });
            setAddForm(null);
        } catch (e) {
            alert(e instanceof Error ? e.message : t('Greška', 'Error'));
        } finally {
            await loadReserved();
        }
    }

    const whOptions = state.all_warehouses?.filter((w: any) => w.is_dispatch_warehouse !== false) ?? [];

    return (
        <>
            <p className="muted" style={{ fontSize: '0.84rem', marginBottom: 10 }}>
                {t(
                    '🔒 Rezerviraj artikle koji su FIZIČKI kod tebe za određenu narudžbu — engine ih izuzima iz L0/L2 free stock pool-a (ne dodjeljuje drugima).',
                    '🔒 Reserve items physically at your branch for a specific order — engine excludes them from L0/L2 free stock pool.',
                )}
            </p>
            <div style={{ marginBottom: 14 }}>
                <label className="filter-label">
                    {t('Poslovnica', 'Branch')}:
                    <select value={whCode} onChange={(e) => setWhCode(e.target.value)} style={{ marginLeft: 6 }}>
                        <option value="">— {t('Odaberi', 'Pick')} —</option>
                        {whOptions.map((w: any) => <option key={w.id} value={w.code}>{w.code} · {w.name}</option>)}
                    </select>
                </label>
            </div>
            {whCode ? (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <h4 style={{ margin: 0, fontSize: '0.86rem' }}>🔒 {t('Trenutno rezervirano', 'Currently reserved')} ({reserved.length})</h4>
                        <button type="button" className="compact" onClick={() => setAddForm({ order_number: '', item_code: '', qty: '', partner_name: '', note: '' })}>
                            + {t('Dodaj rezervaciju', 'Add reservation')}
                        </button>
                    </div>
                    {addForm ? (
                        <div className="card" style={{ padding: '10px 14px', marginBottom: 10, background: '#fffbeb' }}>
                            <h5 style={{ margin: '0 0 8px', fontSize: '0.82rem' }}>{t('Nova rezervacija', 'New reservation')}</h5>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {t('Broj narudžbe', 'Order #')}
                                    <select value={addForm.order_number} onChange={(e) => {
                                        const o = eligible.find((x: any) => x.order_number === e.target.value);
                                        setAddForm({ ...addForm, order_number: e.target.value, partner_name: o?.partner_name ?? addForm.partner_name });
                                    }}>
                                        <option value="">— {t('Odaberi', 'Pick')} —</option>
                                        {eligible.map((o: any) => <option key={o.order_number} value={o.order_number}>{o.order_number} · {o.partner_name?.slice(0, 30)}</option>)}
                                    </select>
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {t('Item code (id)', 'Item code')}
                                    <input value={addForm.item_code} onChange={(e) => setAddForm({ ...addForm, item_code: e.target.value })} placeholder="123" />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {t('Količina', 'Qty')}
                                    <input type="number" step="0.0001" value={addForm.qty} onChange={(e) => setAddForm({ ...addForm, qty: e.target.value })} />
                                </label>
                                <label className="filter-label" style={{ margin: 0 }}>
                                    {t('Napomena', 'Note')}
                                    <input value={addForm.note} onChange={(e) => setAddForm({ ...addForm, note: e.target.value })} />
                                </label>
                            </div>
                            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                                <button type="button" className="compact" onClick={() => void addReserved()} disabled={!addForm.order_number || !addForm.item_code || !addForm.qty}>✓ {t('Spremi', 'Save')}</button>
                                <button type="button" className="secondary compact" onClick={() => setAddForm(null)}>✕ {t('Odustani', 'Cancel')}</button>
                            </div>
                        </div>
                    ) : null}
                    <div className="table-wrap">
                        <table className="admin-user-table" style={{ fontSize: '0.8rem' }}>
                            <thead>
                                <tr>
                                    <th>{t('Narudžba', 'Order')}</th>
                                    <th>{t('Partner', 'Partner')}</th>
                                    <th>{t('Artikl', 'Item')}</th>
                                    <th style={{ textAlign: 'right' }}>{t('Kol.', 'Qty')}</th>
                                    <th>{t('Napomena', 'Note')}</th>
                                    <th>{t('Rezervirao', 'By')}</th>
                                    <th>{t('Kad', 'When')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {reserved.map((r: any) => (
                                    <tr key={r.id}>
                                        <td>{r.order_number}</td>
                                        <td>{r.partner_name ?? '—'}</td>
                                        <td>{r.item_name ?? `#${r.item_code}`}</td>
                                        <td style={{ textAlign: 'right' }}>{r.qty}</td>
                                        <td className="muted" style={{ fontSize: '0.72rem' }}>{r.note ?? '—'}</td>
                                        <td className="muted" style={{ fontSize: '0.72rem' }}>{r.reserved_by_name ?? '—'}</td>
                                        <td className="muted" style={{ fontSize: '0.72rem' }}>{r.reserved_at?.slice(0, 16).replace('T', ' ')}</td>
                                        <td><button type="button" onClick={() => void release(r.id)} className="secondary compact" style={{ color: '#b14040', fontSize: '0.74rem' }}>✕ {t('Otpusti', 'Release')}</button></td>
                                    </tr>
                                ))}
                                {reserved.length === 0 && !loading ? (
                                    <tr><td colSpan={8} className="muted" style={{ textAlign: 'center' }}>{t('Nema rezervacija. Dodaj prvu.', 'No reservations yet.')}</td></tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : (
                <p className="muted" style={{ fontStyle: 'italic' }}>{t('Odaberi poslovnicu da vidiš rezervacije.', 'Pick a branch to see reservations.')}</p>
            )}
        </>
    );
}

function LagerPlanTab({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [editCell, setEditCell] = useState<{ wh: string; weight: number; field: 'mix_pct' | 'is_active'; value: any } | null>(null);
    const [editConfig, setEditConfig] = useState<{ wh: string; field: 'planned_monthly_eur' | 'working_days' | 'trend_factor'; value: any } | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await api<any>('/gold-cm-planner/lager-plan', token));
        } catch { /* */ }
        finally { setLoading(false); }
    }, [token]);
    useEffect(() => { void load(); }, [load]);

    async function patchMix(wh: string, weight: number, patch: any) {
        await mutate('/gold-cm-planner/lager-plan/mix', token, 'PATCH', { warehouse_code: wh, weight_g: weight, ...patch });
        await load();
    }
    async function patchConfig(wh: string, patch: any) {
        await mutate('/gold-cm-planner/lager-plan/config', token, 'PATCH', { warehouse_code: wh, ...patch });
        await load();
    }

    if (loading && !data) return <p className="muted">{t('Učitavam lager plan…', 'Loading lager plan…')}</p>;
    if (!data) return null;

    const fmt = (n: number, dp = 0) => Number(n).toLocaleString(locale === 'en' ? 'en-GB' : 'hr-HR', { minimumFractionDigits: dp, maximumFractionDigits: dp });

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>📊 {t('Lager plan 60×12 — auto-compute standard lager', 'Lager plan 60×12 — auto-compute standard stock')}</h3>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.78rem' }}>
                        {t('Spot @ ', 'Spot @ ')}{data.spot?.date}: <strong>{data.spot?.eur_per_g} €/g</strong> · cycle = {data.cycle_days} dana · formula: daily_eur × mix% × cycle × trend.
                    </p>
                </div>
                <div style={{ fontSize: '0.84rem' }}>
                    Σ <strong>{fmt(data.total_lager_eur)} €</strong> · {fmt(data.total_lager_kom, 0)} kom ukupno
                </div>
            </div>

            {/* k11: SAŽETAK KPI DASHBOARD */}
            {data.summary ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 12 }}>
                    <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '8px 10px' }}>
                        <div style={{ fontSize: '0.66rem', color: 'rgba(0,0,0,0.5)', textTransform: 'uppercase' }}>{t('Σ Mjesečno', 'Σ Monthly')}</div>
                        <strong>{fmt(data.summary.total_monthly_eur)} €</strong>
                    </div>
                    <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '8px 10px' }}>
                        <div style={{ fontSize: '0.66rem', color: 'rgba(0,0,0,0.5)', textTransform: 'uppercase' }}>{t('Σ Dnevno', 'Σ Daily')}</div>
                        <strong>{fmt(data.summary.total_daily_eur)} €</strong>
                    </div>
                    <div style={{ background: '#fff', border: '2px solid #1f7a36', borderRadius: 4, padding: '8px 10px' }}>
                        <div style={{ fontSize: '0.66rem', color: '#1f7a36', textTransform: 'uppercase' }}>{t('Σ Standard lager', 'Σ Standard stock')}</div>
                        <strong style={{ color: '#1f7a36' }}>{fmt(data.summary.total_standard_lager_eur)} €</strong>
                        <div className="muted" style={{ fontSize: '0.7rem' }}>{fmt(data.summary.total_lager_kom, 0)} kom</div>
                    </div>
                    <div style={{ background: '#fff', border: '1px solid var(--ad-acc-3)', borderRadius: 4, padding: '8px 10px' }}>
                        <div style={{ fontSize: '0.66rem', color: 'var(--ad-acc-3)', textTransform: 'uppercase' }}>{t('Σ Reorder', 'Σ Reorder')}</div>
                        <strong style={{ color: 'var(--ad-acc-3)' }}>{fmt(data.summary.total_reorder_eur)} €</strong>
                    </div>
                    <div style={{ background: '#fff', border: '1px solid #1e40af', borderRadius: 4, padding: '8px 10px' }}>
                        <div style={{ fontSize: '0.66rem', color: '#1e40af', textTransform: 'uppercase' }}>{t('Pokrivenost budget', 'Budget coverage')}</div>
                        <strong style={{ color: data.summary.budget_coverage_pct >= 100 ? '#1f7a36' : '#b14040' }}>{data.summary.budget_coverage_pct}%</strong>
                        {data.summary.manjak_eur > 0 ? <div style={{ fontSize: '0.7rem', color: '#b14040' }}>{t('manjak', 'shortfall')} {fmt(data.summary.manjak_eur)} €</div> : null}
                    </div>
                    <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '8px 10px' }}>
                        <div style={{ fontSize: '0.66rem', color: 'rgba(0,0,0,0.5)', textTransform: 'uppercase' }}>{t('Aktivne posl.', 'Active branches')}</div>
                        <strong>{data.summary.active_warehouses}/{data.rows.length}</strong>
                    </div>
                </div>
            ) : null}

            {/* k8: AUTO TREND UPDATE */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <button type="button" className="secondary compact" style={{ fontSize: '0.78rem' }}
                    onClick={async () => {
                        if (!confirm(t('Vuče stvarni promet iz Faros zadnja 4 tjedna i ažurira trend_factor per poslovnica. Nastaviti?', 'Fetch actual sales from Faros last 4 weeks and update trend_factor per branch. Continue?'))) return;
                        try {
                            const r = await mutate<any>('/gold-cm-planner/lager-plan/trend-auto-update', token, 'POST', {});
                            alert(t(`Trend ažuriran za ${r.updated} poslovnica (skipano ${r.skipped}). Cutoff: ${r.cutoff_date}`, `Trend updated for ${r.updated} branches (skipped ${r.skipped}). Cutoff: ${r.cutoff_date}`));
                            await load();
                        } catch (e) { alert(e instanceof Error ? e.message : 'API'); }
                    }}
                >
                    🔄 {t('Auto-update trend (zadnja 4 tjedna stvarnog prometa)', 'Auto-update trend (last 4 weeks actual)')}
                </button>
            </div>

            {/* k9: BUDGET INPUT + RASPODJELA */}
            <div style={{ background: '#fff8e7', border: '1px solid var(--ad-acc)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '0.84rem', color: 'var(--ad-ink)' }}>💰 {t('Raspodjela proračuna', 'Budget allocation')}:</strong>
                <label style={{ fontSize: '0.8rem' }}>
                    {t('Raspoloživi €', 'Available €')}:
                    <input type="number" defaultValue={data.summary?.available_budget_eur ?? 0} onBlur={async (e) => {
                        const v = parseFloat(e.target.value);
                        await mutate('/gold-cm-planner/lager-plan/budget', token, 'PATCH', { available_budget_eur: v });
                        await load();
                    }} style={{ marginLeft: 6, width: 120, padding: '3px 6px', fontSize: '0.8rem' }} />
                </label>
                {data.budget_allocation ? (
                    <span className="muted" style={{ fontSize: '0.78rem' }}>
                        {data.summary.budget_coverage_pct < 100
                            ? t(`⚠ Proračun pokriva ${data.summary.budget_coverage_pct}% — proporcionalna raspodjela aktivirana`, `⚠ Budget covers ${data.summary.budget_coverage_pct}% — proportional allocation active`)
                            : t(`✓ Proračun pokriva 100% potreba`, `✓ Budget covers 100% of needs`)}
                    </span>
                ) : null}
            </div>

            {/* k7: CENTRALNO SKLADIŠTE — senzitivnost po cycle dana */}
            {data.central_model ? (
                <details style={{ marginBottom: 12, background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '6px 10px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700, color: 'var(--ad-ink)' }}>
                        🏬 {t('Centralno skladište — senzitivnost ciklusa', 'Central warehouse — cycle sensitivity')}
                    </summary>
                    <table className="admin-user-table" style={{ fontSize: '0.8rem', marginTop: 6 }}>
                        <thead>
                            <tr>
                                <th>{t('Ciklus dana', 'Cycle days')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Lager centrala €', 'Central stock €')}</th>
                                <th style={{ textAlign: 'right' }}>{t('Approx oz', 'Approx oz')}</th>
                                <th>{t('Napomena', 'Note')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.central_model.map((c: any) => (
                                <tr key={c.cycle_days} style={c.is_default ? { background: 'rgba(31,122,54,0.06)', fontWeight: 600 } : undefined}>
                                    <td>{c.cycle_days}d</td>
                                    <td style={{ textAlign: 'right' }}>{fmt(c.lager_eur)} €</td>
                                    <td style={{ textAlign: 'right' }}>{fmt(c.lager_oz_approx)} oz</td>
                                    <td className="muted" style={{ fontSize: '0.72rem' }}>{c.is_default ? '★ ' + t('preporučeno', 'recommended') : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            ) : null}

            <div className="table-wrap" style={{ overflowX: 'auto', maxHeight: 600 }}>
                <table className="admin-user-table" style={{ fontSize: '0.74rem' }}>
                    <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                        <tr>
                            <th style={{ minWidth: 100, position: 'sticky', left: 0, background: '#fff' }}>{t('Poslovnica', 'Branch')}</th>
                            <th style={{ minWidth: 100 }}>{t('Plan mjeseč. €', 'Plan monthly €')}</th>
                            <th>{t('Dani', 'Days')}</th>
                            <th>{t('Trend', 'Trend')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Dnevno €', 'Daily €')}</th>
                            {data.standard_weights.map((w: number) => (
                                <th key={w} style={{ textAlign: 'center', minWidth: 64 }}>{w}g</th>
                            ))}
                            <th style={{ textAlign: 'right', background: 'rgba(31,122,54,0.08)' }}>Σ €</th>
                            <th style={{ textAlign: 'right', background: 'rgba(31,122,54,0.08)' }}>Σ kom</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((r: any) => (
                            <tr key={r.warehouse_code}>
                                <td style={{ position: 'sticky', left: 0, background: '#fff', fontSize: '0.74rem' }}><strong>{r.warehouse_code}</strong><div className="muted" style={{ fontSize: '0.66rem' }}>{r.warehouse_name?.slice(0, 24)}</div></td>
                                <td>
                                    <input type="number" defaultValue={r.planned_monthly_eur} onBlur={(e) => { const v = parseFloat(e.target.value); if (v !== r.planned_monthly_eur) void patchConfig(r.warehouse_code, { planned_monthly_eur: v }); }} style={{ width: 90, padding: '2px 4px', fontSize: '0.74rem' }} />
                                </td>
                                <td>
                                    <input type="number" defaultValue={r.working_days} onBlur={(e) => { const v = parseInt(e.target.value); if (v !== r.working_days) void patchConfig(r.warehouse_code, { working_days: v }); }} style={{ width: 50, padding: '2px 4px', fontSize: '0.74rem' }} />
                                </td>
                                <td>
                                    <input type="number" step="0.01" defaultValue={r.trend_factor} onBlur={(e) => { const v = parseFloat(e.target.value); if (v !== r.trend_factor) void patchConfig(r.warehouse_code, { trend_factor: v }); }} style={{ width: 60, padding: '2px 4px', fontSize: '0.74rem' }} />
                                </td>
                                <td style={{ textAlign: 'right', fontSize: '0.72rem', color: '#666' }}>{fmt(r.daily_eur, 0)}</td>
                                {data.standard_weights.map((w: number) => {
                                    const cell = r.per_weight[String(w)];
                                    if (!cell) return <td key={w} />;
                                    return (
                                        <td key={w} style={{ textAlign: 'center', background: cell.is_active ? 'rgba(31,122,54,0.04)' : 'rgba(0,0,0,0.02)', padding: 2 }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                <input type="checkbox" checked={cell.is_active} onChange={(e) => void patchMix(r.warehouse_code, w, { is_active: e.target.checked })} style={{ accentColor: '#1f7a36' }} />
                                                <input type="number" step="0.1" min="0" max="100" defaultValue={cell.mix_pct} onBlur={(e) => { const v = parseFloat(e.target.value); if (v !== cell.mix_pct) void patchMix(r.warehouse_code, w, { mix_pct: v }); }} style={{ width: 50, padding: '1px 2px', fontSize: '0.7rem', textAlign: 'right' }} placeholder="%" />
                                                {cell.is_active && cell.lager_eur > 0 ? (
                                                    <span style={{ fontSize: '0.66rem', color: '#1f7a36', fontWeight: 600 }}>{fmt(cell.lager_eur, 0)}€ / {fmt(cell.lager_kom, 1)}kom</span>
                                                ) : null}
                                            </div>
                                        </td>
                                    );
                                })}
                                <td style={{ textAlign: 'right', fontWeight: 700, background: 'rgba(31,122,54,0.08)' }}>{fmt(r.total_lager_eur)} €</td>
                                <td style={{ textAlign: 'right', fontWeight: 700, background: 'rgba(31,122,54,0.08)' }}>{fmt(r.total_lager_kom, 0)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="muted" style={{ fontSize: '0.72rem', marginTop: 6 }}>
                ℹ️ {t(
                    'Auto-compute: daily_eur = plan_mjes / dani. Per gramaža: lager = daily × mix% × cycle × trend. Kom = lager / (spot × g). Checkbox = drži li se ta gramaža na lageru.',
                    'Auto: daily_eur = plan_monthly / days. Per weight: stock = daily × mix% × cycle × trend. Pcs = stock / (spot × g).',
                )}
            </p>
        </>
    );
}
